/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  CellValueChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellEditorParams,
  ICellRendererParams,
  IRowNode,
} from '@ag-grid-community/core';
import { ValueSetterParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { AsyncPipe, formatDate, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  Injector,
  model,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import dayjs, { Dayjs } from 'dayjs';
import {
  first as _first,
  map as _map,
  filter,
  groupBy,
  includes,
  isArray,
  isEqual,
  isObject,
  isUndefined,
  uniq,
} from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  filter as filterRxjs,
  firstValueFrom,
  from,
  of,
  Subject,
} from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';

import { BudgetService } from '@features/budget/services/budget.service';
import { getTrialAttributes } from '@features/budget-attributes/services/budget-attributes.service';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { MultipleVendorSelectComponent } from '@features/multiple-vendor-select/multiple-vendor-select.components';
import { ProgressTrackerComponent } from '@features/progress-tracker/progress-tracker.component';
import { fetchInProgressEvents } from '@features/progress-tracker/utils/fetch-in-progress-events';

import { BudgetQuery } from '@models/budget/budget.query';
import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { TrialSettingsService } from '@models/trial-settings/trial-settings.service';

import { AnalyticCard } from '@shared/components/analytic-card/analytic-card';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ColumnChooserComponent } from '@shared/components/column-chooser/column-chooser.component';
import { ColumnChooserNode } from '@shared/components/column-chooser/components/column-chooser-modal/model/column-chooser.model';
import { CustomMapper } from '@shared/components/column-chooser/components/column-chooser-modal/model/custom-chooser.model';
import { injectColumnChooserConfigName } from '@shared/components/column-chooser/directives/ima-config-name.directive';
import { ColumnChooserService } from '@shared/components/column-chooser/services/column-chooser.service';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  SummaryAccordion,
  SummaryRow,
} from '@shared/components/summary-accordion/summary-accordion';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { FOOTER_HEIGHT } from '@shared/constants/layout.constants';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { EMPTY_UUID } from '@shared/constants/uuid';
import { AuthorizeDirective } from '@shared/directives/authorize.directive';
import { GridColumnWidthPersistenceDirective } from '@shared/directives/grid-column-width-persistence.directive';
import { StickyFixedHeightGridDirective } from '@shared/directives/sticky-grid/sticky-fixed-height-table.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { ApiService } from '@shared/services/api.service';
import {
  ActivityType,
  AdjustmentType,
  AmountType,
  batchCreateBudgetExpensesMutation,
  batchCreateNotesMutation,
  BudgetExpenseData,
  BudgetExpenseInput,
  CreateNoteInput,
  Currency,
  DocumentType,
  EntityType,
  EventType,
  ExpenseNoteType,
  ExpenseSourceType,
  ExpenseType,
  GqlService,
  InvoiceCategoryTotals,
  listDiscountExpensesQuery,
  listDocumentsQuery,
  listExpenseSourceSettingsQuery,
  listInMonthExpensesQuery,
  listTrialVendorTimelineQuery,
  listUserNamesWithEmailQuery,
  Note,
  NoteType,
  PermissionType,
  Site,
  updateAccrualsMutation,
  User,
  VendorEstimateSummary,
  WorkflowStep,
} from '@shared/services/gql.service';
import { InvestigatorTransactionsService } from '@shared/services/investigator-transactions/investigator-transactions.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseAdjustmentsService } from '@shared/services/quarter-close-adjustments.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { ClosingPageStore } from '@shared/store/closing-page/closing-page.store';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import {
  AgEditFirstRow,
  decimalAdd,
  decimalDifference,
  decimalDivide,
  decimalMultiply,
} from '@shared/utils';
import { applyColumnDefsWithPinState } from '@shared/utils/ag-grid-column-pin.util';
import { agGridAllVisibleData } from '@shared/utils/ag-grid-data.utils';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { selectWithGuardChangesModalCheck } from '@shared/utils/select-option';
import { ExportType, KeysMatching, Maybe, Utils } from '@shared/utils/utils';

import { PeriodCloseComponent, QuarterDate } from '../../period-close.component';
import {
  AdjustmentModalComponent,
  AdjustmentModalResponseType,
} from './adjustment-modal/adjustment-modal.component';
import { ImaTableGroups } from './enum/ima-table-gropes';
import { inMonthAdjustmentGridConfig } from './grid-config/ima-grid-config';
import {
  InMonthAdjustmentGridConfigContext,
  InvoiceExpenseSourceFilterType,
} from './grid-config/ima-grid-config-context';
import { NoteModalComponent, NoteModalResponseType } from './note-modal/note-modal.component';
import { ExpenseSource, QuarterCloseAdjustmentGridData } from './quarter-close-adjustments.model';
import { AccrualsService } from './services/accruals.service';
import { ExpenseSourceService } from './services/expense-source.service';
import { ImaTaskCalculationManagerService } from './services/ima-task-calculation-manager.servce';
import { MonthExpensesService } from './services/month-expenses.service';
import { SupportModalComponent } from './support-modal/support-modal.component';
import { AddVendorEstimateUploadComponent } from '../quarter-close/add-vendor-estimate-upload/add-vendor-estimate-upload.component';
import { VendorEstimateUploadComponent } from '../quarter-close/vendor-estimate-upload/vendor-estimate-upload.component';
import { DiscountExpenseDetail } from '../quarter-close-checklist/components/checklist-section-discount/checklist-section-discount.component';
import { QuarterCloseChecklistVendorService } from '../quarter-close-checklist/services/quarter-close-checklist-vendor.service';

const expenseSourceMapping = {
  [ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED]: ExpenseSource['Evidence Based'],
  [ExpenseSourceType.EXPENSE_SOURCE_FORECAST]: ExpenseSource.Forecast,
  [ExpenseSourceType.EXPENSE_SOURCE_INVOICE]: ExpenseSource.Invoice,
  [ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT]: ExpenseSource.Manual,
  [ExpenseSourceType.EXPENSE_SOURCE_NONE]: ExpenseSource.None,
  [ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE]: ExpenseSource['Vendor Estimate'],
};

const expenseSourceReverseMapping = {
  [ExpenseSource.Forecast]: ExpenseSourceType.EXPENSE_SOURCE_FORECAST,
  [ExpenseSource.Invoice]: ExpenseSourceType.EXPENSE_SOURCE_INVOICE,
  [ExpenseSource.Manual]: ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT,
  [ExpenseSource.None]: ExpenseSourceType.EXPENSE_SOURCE_NONE,
  [ExpenseSource['Evidence Based']]: ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED,
  [ExpenseSource['Vendor Estimate']]: ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE,
};

type CalculableColumns = KeysMatching<QuarterCloseAdjustmentGridData, number>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-[21px] p-[21px]',
  },
  imports: [
    AsyncPipe,
    TablePanelComponent,
    InputComponent,
    ReactiveFormsModule,
    ExportExcelButtonComponent,
    ColumnChooserComponent,
    AuthorizeDirective,
    TooltipDirective,
    ButtonComponent,
    NgSelectComponent,
    NgOptionTemplateDirective,
    MultipleVendorSelectComponent,
    VendorDropdownComponent,
    StickyFixedHeightGridDirective,
    AgGridAngular,
    NgStyle,
    IconComponent,
    MenuComponent,
    ProgressTrackerComponent,
    ErrorsTooltipComponent,
    AnalyticCard,
    GridColumnWidthPersistenceDirective,
    SummaryAccordion,
  ],
  providers: [
    ImaTaskCalculationManagerService,
    AccrualsService,
    ExpenseSourceService,
    MonthExpensesService,
    TrialSettingsService,
  ],
  selector: 'aux-quarter-close-adjustments',
  styleUrls: ['./quarter-close-adjustments.component.scss'],
  templateUrl: './quarter-close-adjustments.component.html',
})
export class QuarterCloseAdjustmentsComponent implements OnDestroy, OnInit {
  accrualsService = inject(AccrualsService);
  destroyRef = inject(DestroyRef);
  expenseSourceService = inject(ExpenseSourceService);
  globalStore = inject(GlobalStore);
  imaTaskCalculationManagerService = inject(ImaTaskCalculationManagerService);
  investigatorTransactionsService = inject(InvestigatorTransactionsService);
  monthExpensesService = inject(MonthExpensesService);
  periodCloseComponent = inject(PeriodCloseComponent);
  sitesQuery = inject(SitesQuery);
  trialSettingsService = inject(TrialSettingsService);

  private apiService = inject(ApiService);
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private budgetQuery = inject(BudgetQuery);
  private budgetService = inject(BudgetService);
  private cdr = inject(ChangeDetectorRef);
  private closingPageStore = inject(ClosingPageStore);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private injector = inject(Injector);
  private invoiceService = inject(InvoiceService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationQuery = inject(OrganizationQuery);
  private overlayService = inject(OverlayService);
  private periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private quarterCloseAdjustmentsService = inject(QuarterCloseAdjustmentsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sitesService = inject(SitesService);
  private vendorService = inject(QuarterCloseChecklistVendorService);
  private workflowQuery = inject(WorkflowQuery);

  columnChooserConfigName = injectColumnChooserConfigName('quarter-close-adjustments-grid');
  flags = injectFlags({
    showAvailableInvoice: 'adjustments_available_invoice',
    showExpenseDetailAnalytics: 'expense_detail_analytics',
    showUnitTotals: 'adjustments_unit_totals',
    showUOM: 'adjustments_unit_of_measure',
    showVendorEstimateAmount: 'vendor_estimate_dollars_ltd',
    showVendorEstimatePercent: 'vendor_estimate_percent_ltd',
  });
  permissions = injectPermissions({
    editVendorEstimate: 'PERMISSION_EDIT_VENDOR_ESTIMATE',
    modifyOpenMonthAdjustments: 'PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS',
    vendorEstimateUpload: 'PERMISSION_UPLOAD_VENDOR_ESTIMATE',
  });

  editButtonReasonsToDisabled = signal<string[]>([]);
  editMode = signal(false);
  excludeColumnChooserColumns = signal<string[]>([ImaTableGroups.VENDOR_CONTRACT]);
  expandingRows = signal(false);
  expenseSourceFilterValues = signal<Set<string>>(new Set());
  filterChangeTrigger = signal(0);
  gridDataLoading = signal(false);
  invoiceExpenseSourceFilter = signal<InvoiceExpenseSourceFilterType>({});
  notesFilterValues = signal<Set<string>>(new Set());
  overBudgetFilterValue = signal<boolean>(false);
  removeVendorEstimateLoading = signal(false);
  tableHeight = signal(0);
  timelineLoading = signal(false);
  vendorEstimatesLoading = signal(false);

  allEditableColumnsHidden$ = new BehaviorSubject(false);

  allEditableColumnsHidden = toSignal(this.allEditableColumnsHidden$, {
    requireSync: true,
  });

  selected_month = new FormControl('');
  vendorEstimateSupportingDoc$ = new BehaviorSubject<listDocumentsQuery['items']>([]);

  doesSelectedMonthHasVendorEstimateSupportingDoc$ = combineLatest([
    this.vendorEstimateSupportingDoc$,
    this.selected_month.valueChanges.pipe(startWith(this.selected_month.value)),
  ]).pipe(
    map(([estimates]) => {
      const selectedMonth = this.selected_month.value || '';

      return !!estimates.filter(
        (estimate) => estimate.target_date?.slice(0, 7) === selectedMonth.slice(0, 7)
      ).length;
    })
  );

  doesSelectedMonthHasVendorEstimateSupportingDoc = toSignal(
    this.doesSelectedMonthHasVendorEstimateSupportingDoc$
  );

  gridApi$ = new BehaviorSubject<GridApi | null>(null);

  gridApi = toSignal(this.gridApi$, { requireSync: true });

  gridData$ = new BehaviorSubject<QuarterCloseAdjustmentGridData[]>([]);

  gridData = toSignal(this.gridData$, { requireSync: true });

  isSelectedMonthOpen$ = new BehaviorSubject<boolean>(false);

  isSelectedMonthOpen = toSignal(this.isSelectedMonthOpen$);

  isSelectedMonthOpenOrFuture$ = new BehaviorSubject<boolean>(false);

  isSelectedMonthOpenOrFuture = toSignal(this.isSelectedMonthOpenOrFuture$);

  selected_category = new FormControl('');

  selectedCategory = toSignal(
    this.selected_category.valueChanges.pipe(startWith(this.selected_category.value)),
    {
      initialValue: '',
    }
  );

  selected_threshold = new FormControl('');

  selectedThreshold = toSignal(
    this.selected_threshold.valueChanges.pipe(startWith(this.selected_threshold.value)),
    {
      initialValue: '',
    }
  );

  selected_vendor = new FormControl<string | string[]>([]);

  shouldShowEvidenceColumns$ = combineLatest([
    this.sitesQuery.selectAll(),
    this.selected_vendor.valueChanges,
    this.selected_category.valueChanges.pipe(startWith(this.selected_category.value)),
  ]).pipe(
    map(([sites, , category]) => {
      const ids = sites.map((site) => site.managed_by_id);

      return (
        (category === ActivityType.ACTIVITY_INVESTIGATOR || category === '') &&
        !!this.selectedVendors.some((vendor) => ids.includes(vendor))
      );
    })
  );

  shouldShowEvidenceColumns = toSignal(this.shouldShowEvidenceColumns$);

  vendorEstimates$ = new BehaviorSubject<VendorEstimateSummary[]>([]);

  vendorEstimates = toSignal(this.vendorEstimates$, { requireSync: true });

  vendors$ = this.budgetQuery.select('budget_info').pipe(
    map((data) => {
      const allVendors = this.organizationQuery.getAllVendors();

      return data
        .map((info) => ({
          id: info.vendor_id || '',
          name: allVendors.find((vendor) => vendor.id === info.vendor_id)?.name || info.name || '',
        }))
        .sort((a, b) =>
          Utils.alphaNumSort(a.name?.toUpperCase() ?? '', b.name?.toUpperCase() ?? '')
        );
    })
  );
  vendorsOptions$ = this.vendors$.pipe(
    map((vendors) => vendors.map((v) => ({ label: v.name || '', value: v.id || '' })))
  );

  vendorOptions = toSignal(this.vendorsOptions$, {
    initialValue: [] as { label: string; value: string }[],
  });

  expenseLtdTitle = computed(() => {
    const month = this.selected_month.value;

    if (!month) return 'Expense LTD incl. [Month YYYY]';

    return `Expense LTD incl. ${dayjs(month).format('MMM YYYY')}`;
  });
  filteredGridData = computed(() => {
    const gridApi = this.gridApi();
    const gridData = this.gridData();

    // Trigger recalculation when filters change
    this.filterChangeTrigger();

    if (!gridApi || !gridData || gridData.length === 0) return [];

    const filteredData: QuarterCloseAdjustmentGridData[] = [];

    gridApi.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredData.push(node.data);
      }
    });

    return filteredData;
  });
  gridCurrencies = computed(() => {
    const gridData = this.gridData();

    if (!gridData || gridData.length === 0) return new Set<Currency>();

    return new Set(gridData.map((row) => row.vendorCurrency));
  });
  expenseLtdTotalFormatted = computed(() => {
    const currency = this.gridCurrencies().values().next().value;
    const filteredData = this.filteredGridData();

    if (!filteredData || filteredData.length === 0) return '';

    const total = filteredData.reduce((total, row) => {
      if (row.expense_ltd && typeof row.expense_ltd === 'number') {
        return total + row.expense_ltd;
      }

      return total;
    }, 0);

    return new MoneyPipe().transform(total, currency, false);
  });
  expenseSourceSummaryRows = computed(() => {
    const gridData = this.gridData();
    const filteredData = this.filteredGridData();
    const filterValues = this.expenseSourceFilterValues();
    const currency = this.gridCurrencies().values().next().value;

    if (!gridData || gridData.length === 0) return [];

    const sourceGroups = groupBy(gridData, 'tma_source');
    const filteredSourceGroups = groupBy(filteredData, 'tma_source');

    const summaryRows: SummaryRow[] = [];

    Object.entries(sourceGroups).forEach(([source, rows]) => {
      const activityCount = rows.length;
      const filteredRows = filteredSourceGroups[source] || [];
      const filteredActivityCount = filteredRows.length;
      const monthlyExpense = filteredRows.reduce((total, row) => {
        if (row.total_monthly_expense && typeof row.total_monthly_expense === 'number') {
          return total + row.total_monthly_expense;
        }

        return total;
      }, 0);

      // Only include sources with activities (hide zero activities)
      if (activityCount > 0) {
        const isChecked = filterValues.has(source);

        summaryRows.push({
          checked: isChecked,
          id: source,
          label: source,
          value1: isChecked ? filteredActivityCount.toString() : 'N/A',
          value2: isChecked ? new MoneyPipe().transform(monthlyExpense, currency, false) : 'N/A',
        });
      }
    });

    // Sort alphabetically with 'None' forced to bottom
    return summaryRows.sort((a, b) => {
      // Force 'None' to bottom
      if (a.label === 'None' && b.label !== 'None') return 1;

      if (b.label === 'None' && a.label !== 'None') return -1;

      // Alphabetical sort for all other cases
      return a.label.localeCompare(b.label);
    });
  });
  hasMultipleCurrencies = computed(() => this.gridCurrencies().size > 1);

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(
    () =>
      this.trialAttributesLoading() ||
      this.gridDataLoading() ||
      this.vendorEstimatesLoading() ||
      this.timelineLoading()
  );
  monthlyExpenseTotalFormatted = computed(() => {
    const currency = this.gridCurrencies().values().next().value;
    const filteredData = this.filteredGridData();

    if (!filteredData || filteredData.length === 0) return '';

    const total = filteredData.reduce((total, row) => {
      if (row.total_monthly_expense && typeof row.total_monthly_expense === 'number') {
        return total + row.total_monthly_expense;
      }

      return total;
    }, 0);

    return new MoneyPipe().transform(total, currency, false);
  });
  monthlyExpenseTrendConfig = computed(() => {
    const filteredData = this.filteredGridData();
    const currency = this.gridCurrencies().values().next().value;

    if (!filteredData || filteredData.length === 0) {
      return {
        direction: 'up' as const,
        label: 'Variance to Previous Month',
        percentage: '',
        showDirection: false,
        showPercentage: true,
        showValue: true,
        value: '',
      };
    }

    let totalVariance = 0;
    let totalPrevMonthAmount = 0;
    let totalMonthlyExpense = 0;

    for (const row of filteredData) {
      if (row.variance_to_previous_month && typeof row.variance_to_previous_month === 'number') {
        totalVariance += row.variance_to_previous_month;
      }

      if (row.prev_month_amount && typeof row.prev_month_amount === 'number') {
        totalPrevMonthAmount += row.prev_month_amount;
      }

      if (row.total_monthly_expense && typeof row.total_monthly_expense === 'number') {
        totalMonthlyExpense += row.total_monthly_expense;
      }
    }

    const percentage = Utils.percentageFormatter(
      this.calcVarianceToPreviousMonthPercentage(totalMonthlyExpense, totalPrevMonthAmount) || 0,
      {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }
    );

    const direction = totalVariance >= 0 ? ('up' as const) : ('down' as const);

    const value = new MoneyPipe().transform(Math.abs(totalVariance), currency, false);

    return {
      direction,
      label: 'Variance to Previous Month',
      percentage,
      showDirection: value !== ZERO_HYPHEN,
      showPercentage: true,
      showValue: true,
      value,
    };
  });
  notesSummaryRows = computed(() => {
    const gridData = this.gridData();
    const filteredData = this.filteredGridData();
    const filterValues = this.notesFilterValues();
    const currency = this.gridCurrencies().values().next().value;

    if (!gridData || gridData.length === 0) {
      // Empty state - show both options with long dashes
      return [
        {
          checked: filterValues.has('with-notes'),
          id: 'with-notes',
          label: 'Activities with notes',
          value1: '',
          value2: '',
        },
        {
          checked: filterValues.has('without-notes'),
          id: 'without-notes',
          label: 'Activities without notes',
          value1: '',
          value2: '',
        },
      ];
    }

    // Separate activities with and without notes
    const activitiesWithNotes = filteredData.filter((row) => row.notes && row.notes.length > 0);
    const activitiesWithoutNotes = filteredData.filter(
      (row) => !row.notes || row.notes.length === 0
    );

    const withNotesExpense = activitiesWithNotes.reduce((total, row) => {
      if (row.total_monthly_expense && typeof row.total_monthly_expense === 'number') {
        return total + row.total_monthly_expense;
      }

      return total;
    }, 0);

    const withoutNotesExpense = activitiesWithoutNotes.reduce((total, row) => {
      if (row.total_monthly_expense && typeof row.total_monthly_expense === 'number') {
        return total + row.total_monthly_expense;
      }

      return total;
    }, 0);

    return [
      {
        checked: filterValues.has('with-notes'),
        id: 'with-notes',
        label: 'Activities with notes',
        value1: filterValues.has('with-notes')
          ? activitiesWithNotes.length === 0
            ? ZERO_HYPHEN
            : activitiesWithNotes.length.toString()
          : 'N/A',
        value2: filterValues.has('with-notes')
          ? new MoneyPipe().transform(withNotesExpense, currency, false)
          : 'N/A',
      },
      {
        checked: filterValues.has('without-notes'),
        id: 'without-notes',
        label: 'Activities without notes',
        value1: filterValues.has('without-notes')
          ? activitiesWithoutNotes.length === 0
            ? ZERO_HYPHEN
            : activitiesWithoutNotes.length.toString()
          : 'N/A',
        value2: filterValues.has('without-notes')
          ? new MoneyPipe().transform(withoutNotesExpense, currency, false)
          : 'N/A',
      },
    ];
  });

  editableTMAColumns: (keyof QuarterCloseAdjustmentGridData)[] = [
    'tma_unit',
    'tma_amount',
    'tma_source',
    'historical_adjustment_amount',
  ];

  possibleEditColumns = computed(() => {
    const columns: (keyof QuarterCloseAdjustmentGridData)[] = [...this.editableTMAColumns];
    let veCanBeEdited = false;
    let tmaCanBeEdited = false;

    if (this.hasEditPermissions().hasEditVendorPermission && !this.isVendorEstimatesLocked()) {
      columns.push(...this.editableVeColumns);
      veCanBeEdited = true;
    }

    if (this.hasEditPermissions().hasEditTmaPermission && !this.isWorkflowLocked()) {
      columns.push(...this.editableTMAColumns);
      tmaCanBeEdited = true;
    }

    return { columns, tmaCanBeEdited, veCanBeEdited };
  });

  selectedVendorIDs = model<string[]>([]);

  doesSelectedMonthHasVendorEstimate = () => {
    const estimates = this.vendorEstimates();

    const selectedVendor = this.selectedVendorIDs();

    return !!estimates.filter(
      (estimate) =>
        selectedVendor.includes(estimate.organization_id) && estimate.vendor_estimate_exists
    ).length;
  };

  selectedMonthHasVendorEstimate = computed(() => this.doesSelectedMonthHasVendorEstimate());

  afterOnSave = new BehaviorSubject(false);
  BOTTOM_CONTAINER_PADDING = 25;
  bottomRowData$ = new BehaviorSubject<null | Record<
    CalculableColumns & { vendorCurrency: Currency },
    number
  >>({} as Record<CalculableColumns & { vendorCurrency: Currency }, number>);
  budgetTemplateUploadProgressId = fetchInProgressEvents(
    EventType.VENDOR_ESTIMATE_TEMPLATE_UPLOADED,
    this.destroyRef
  );
  cantEditMessage =
    'Month was not closed in the Auxilius Product. Budget and vendor expense data not available on Expense Detail.';
  defaultCategories: { label: string; value: '' | ActivityType }[] = [
    { label: 'All', value: '' },
    { label: 'Services', value: ActivityType.ACTIVITY_SERVICE },
    { label: 'Discount', value: ActivityType.ACTIVITY_DISCOUNT },
    { label: 'Pass-through', value: ActivityType.ACTIVITY_PASSTHROUGH },
    { label: 'Investigator', value: ActivityType.ACTIVITY_INVESTIGATOR },
  ];
  categories: { label: string; value: '' | ActivityType }[] = this.defaultCategories;
  columnChooserGrouping: Record<string, CustomMapper> = {
    attributes: {
      ignoreChildrenSelection: true,
    },
    [ImaTableGroups.CATEGORY]: {
      ignoreChildrenSelection: true,
    },
    [ImaTableGroups.VENDOR_CONTRACT]: {
      ignoreChildrenSelection: true,
    },
  };
  discountExpenses$ = new BehaviorSubject<listDiscountExpensesQuery[]>([]);
  editableVeColumns: (keyof QuarterCloseAdjustmentGridData)[] = [
    'vendor_estimate_percent_ltd',
    'vendor_estimate_amount',
    'vendor_estimate_amount_ltd',
  ];
  editButtonDisabled$ = new BehaviorSubject(false);
  editedOldGridRow = new Map<
    string,
    {
      historical_adjustment_amount: number;
      tma_amount: number;
      tma_unit_cost: number;
      vendor_estimate_amount: number;
    }
  >();
  editMode$ = new BehaviorSubject(false);
  expenseSettings: Array<listExpenseSourceSettingsQuery> = [];
  gridDataLoading$ = toObservable(this.gridDataLoading);
  gridDataProcessing$ = new BehaviorSubject(false);
  gridOptions$ = new BehaviorSubject<GridOptions | null>(null);
  gridUpdated = true;
  groupedActivitiesByActivityTypeAndVendor = new Map<string, QuarterCloseAdjustmentGridData[]>();
  hasEditPermissions = this.authService.getPermissions({
    hasEditTmaPermission: PermissionType.PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS,
    hasEditVendorPermission: PermissionType.PERMISSION_EDIT_VENDOR_ESTIMATE,
  });
  hasEditVendorEstimatePermission$ = new BehaviorSubject<boolean>(false);
  hideVendorEstimateAmountLTD$ = new BehaviorSubject(true);
  hideVendorEstimatePercentLTD$ = new BehaviorSubject(true);
  historicalAdjustmentToBeDeleted = new Set<string>();
  inMonthExpenses$ = new BehaviorSubject<listInMonthExpensesQuery[]>([]);
  invoiceCategoryTotalsByVendor: Map<string, InvoiceCategoryTotals> = new Map();
  isAutoHistoricalAdjustmentsEnabled = false;
  isSelectedCategoryDiscount$ = this.selected_category.valueChanges.pipe(
    startWith(this.selected_category.value),
    map(() => this.selected_category.value === ActivityType.ACTIVITY_DISCOUNT)
  );
  isVendorEstimateExpandChange$ = new BehaviorSubject(true);
  isVendorEstimatesLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_VENDOR_ESTIMATES
  );
  isVendorEstimatesLocked$ = new BehaviorSubject(false);
  isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
  );
  isWorkflowLocked$ = this.workflowQuery.getLockStatusByWorkflowStepType$(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
  );
  manualsToBeDeleted = new Set<string>();
  materialityThresholds: { label: string; value: number | string }[] = [
    { label: 'All', value: '' },
    { label: '>$100,000', value: 100_000 },
    { label: '>$50,000', value: 50_000 },
    { label: '>$25,000', value: 25_000 },
    { label: '>$10,000', value: 10_000 },
    { label: '>$5,000', value: 5_000 },
  ];
  months: (QuarterDate & { label: string })[] = [];
  @ViewChild('monthSelect') monthSelect?: NgSelectComponent;
  multipleVendorsActive = this.launchDarklyService.$select(
    (flags) => flags.ima_filter_multiple_vendors
  );
  oneColumnWidthStrategy = {
    vendorEstimate: 120,
  };
  readonly overlayLoadingTemplate = '<span></span>';
  rows = {
    edited: new Set<string>(),
    historicalAdjustmentChanged: new Set<string>(),
    sourceChanged: new Set<string>(),
    totalChanged: new Set<string>(),
    unitChanged: new Set<string>(),
    vendorEstimateAmountLtdChanged: new Set<{ activity_id: string; time: Dayjs }>(),
    vendorEstimateChanged: new Set<{ activity_id: string; time: Dayjs }>(),
    vendorEstimateEffected: new Set<string>(),
    vendorEstimatePercentChanged: new Set<{ activity_id: string; time: Dayjs }>(),
  };
  saveCheck$ = new BehaviorSubject(false);
  search = new FormControl('');
  showCantEdit = false;
  timelineLoading$ = toObservable(this.timelineLoading);
  timelines$ = new BehaviorSubject<listTrialVendorTimelineQuery[]>([]);
  userHasAdjustPermission$ = new BehaviorSubject<boolean>(false);
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();
  vendorEstimateSelectableCategories = new Set<`${ActivityType}_${string}`>();
  vendorEstimateSupportingDocUploaded$ = new Subject<void>();

  private isSelectedMonthFuture = false;

  ngOnInit(): void {
    this.workflowQuery
      .getLockStatusByWorkflowStepType$(WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_VENDOR_ESTIMATES)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isVendorEstimatesLocked) =>
        this.isVendorEstimatesLocked$.next(isVendorEstimatesLocked)
      );

    this.periodCloseComponent.selectedMonth$
      .pipe(
        distinctUntilChanged(),
        switchMap((data) => {
          if (data) {
            const { category, month, vendor } = data;

            return of({
              category: category.replace('CATEGORY', 'ACTIVITY'),
              month: dayjs(month).date(3).format('YYYY-MM-DD'),
              vendor,
            });
          }

          return combineLatest([
            this.vendors$,
            this.periodCloseComponent.quartersObjUpdated$.pipe(startWith(null)),
          ]).pipe(
            switchMap(([vendors]) => {
              if (!this.months.length) {
                const bool = this.updateMonths();

                if (!bool) {
                  return EMPTY;
                }
              }

              // If navigating from Period Close Checklist, we
              // need to filter by vendor id.

              // First, we need to confirm the incoming vendor id is available.
              // If it isn't, we'll default to the original functionality:
              // (selecting the first available vendor in the vendor array).

              const { filterByVendorId } = this.vendorService;
              let selectedVendorId = '';
              let IsVendorIdParamPresent = false;

              if (filterByVendorId) {
                IsVendorIdParamPresent = true;
                const vendorIds = vendors.map((vendor) => vendor.id);

                if (vendorIds.includes(filterByVendorId)) {
                  selectedVendorId = filterByVendorId;
                }

                this.vendorService.filterByVendorId = '';
              }

              if (!selectedVendorId) {
                selectedVendorId = vendors[0].id || '';
              }

              const persistedMonth = this.periodCloseService.getSelectedQuarterMonth(
                this.router.url
              );
              const persistedQuarterMonth = dayjs(persistedMonth).date(3).format('YYYY-MM-DD');

              const obj = {
                category: this.selected_category.value,
                month: persistedQuarterMonth,
                vendor: selectedVendorId,
              } as { category: string; month: string; vendor: string };

              // find out if we have selected month, category, vendor
              const overrideDefaultIfNecessary = (
                field: keyof typeof obj,
                fc: FormControl,
                arr: string[]
              ) => {
                if (fc.value) {
                  const bool = !!arr.filter((val) => val === fc.value).length;

                  if (bool) {
                    if (IsVendorIdParamPresent && field === 'vendor') {
                      obj[field] = selectedVendorId;
                    } else {
                      obj[field] = fc.value;
                    }
                  }
                }
              };

              overrideDefaultIfNecessary(
                'month',
                this.selected_month,
                this.months.map((m) => m.iso)
              );
              overrideDefaultIfNecessary(
                'category',
                this.selected_category,
                this.categories.map((c) => c.value)
              );

              // Take vendor from persis service first
              if (
                !this.quarterCloseAdjustmentsService.formControlSelectedVendorSource.value.length
              ) {
                overrideDefaultIfNecessary(
                  'vendor',
                  this.selected_vendor,
                  vendors.map((v) => v.id || '')
                );
              } else {
                obj.vendor = '';
              }

              return of(obj);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ category, month, vendor }) => {
        this.selected_month.setValue(month);
        this.selected_category.setValue(category);

        if (vendor) {
          this.setSelectedVendor([vendor]);
        }

        // This is here to prevent issues like AUXI-3632 and AUXI-3631
        this.periodCloseComponent.selectedMonth$.next(null);
      });

    this.updateFiltersOnRouterQueryParams();

    this.mainQuery
      .select('trialKey')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.editMode$.next(false);
        this.editMode.set(false);
        this.saveCheck$.next(false);
      });

    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => {
        if (this.budgetQuery.getValue().budget_data.length && this.vendorEstimateColumnChecker()) {
          this.onUploadVendorEstimate().catch(console.error);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.closingPageStore.update({ adjustmentHasUnsavedChanges: false });
  }

  private applyExpenseSourceFilter(rowId?: string, checked?: boolean): void {
    const gridApi = this.gridApi$.value;

    if (!gridApi) return;

    let filterValues = this.expenseSourceFilterValues();

    // If rowId and checked are provided, update the filter values
    if (rowId !== undefined && checked !== undefined) {
      if (checked) {
        filterValues = new Set([rowId, ...filterValues]);
      } else {
        const newFilterValues = new Set(filterValues);

        newFilterValues.delete(rowId);
        filterValues = newFilterValues;
      }
    }

    // Update the signal with the new filter values
    this.expenseSourceFilterValues.set(filterValues);

    gridApi.setFilterModel({
      ...gridApi.getFilterModel(),
      tma_source: {
        filterType: 'set',
        values: Array.from(filterValues),
      },
    });

    gridApi.onFilterChanged();
  }

  private applyNotesFilter(rowId?: string, checked?: boolean): void {
    const gridApi = this.gridApi$.value;

    if (!gridApi) return;

    let filterValues = this.notesFilterValues();

    // If rowId and checked are provided, update the filter values
    if (rowId !== undefined && checked !== undefined) {
      if (checked) {
        filterValues = new Set([rowId, ...filterValues]);
      } else {
        const newFilterValues = new Set(filterValues);

        newFilterValues.delete(rowId);
        filterValues = newFilterValues;
      }
    }

    // Update the signal with the new filter values
    this.notesFilterValues.set(filterValues);

    // Create filter model for AG Grid
    let notesFilter = null;

    if (filterValues.has('with-notes') && filterValues.has('without-notes')) {
      // Show all rows - no filter needed
      notesFilter = null;
    } else if (filterValues.has('with-notes')) {
      // Show only rows with notes (notes count > 0)
      notesFilter = {
        filter: 0,
        type: 'greaterThan',
      };
    } else if (filterValues.has('without-notes')) {
      // Show only rows without notes (notes count = 0)
      notesFilter = {
        filter: 0,
        type: 'equals',
      };
    } else if (filterValues.size === 0) {
      // Show no rows
      notesFilter = {
        filter: 0,
        type: 'lessThan',
      };
    }

    // Apply the filter
    gridApi.setFilterModel({
      ...gridApi.getFilterModel(),
      notes: notesFilter,
    });

    gridApi.onFilterChanged();
  }

  private applyOverBudgetFilter(checked: boolean): void {
    const gridApi = this.gridApi$.value;

    if (!gridApi) return;

    // Update the signal with the new filter value
    this.overBudgetFilterValue.set(checked);

    // Use external filter instead of column filter
    gridApi.onFilterChanged();
  }

  beforeFilterChanges(saveCheck: boolean) {
    return async () => {
      if (saveCheck) {
        const result = this.overlayService.openUnsavedChangesConfirmation();
        const event = await firstValueFrom(result.afterClosed$);

        return !!event.data;
      }

      return true;
    };
  }

  // eslint-disable-next-line max-params
  calculateRow(
    rawNewValue: number,
    activityId: string,
    colDefField: string | undefined,
    pTmaUnitCost: number,
    pHistoricalAdjustmentAmount: number,
    vendor_estimate_amount: number,
    oldValue: number,
    tmaUnit: number,
    tmaAmount: number,
    totalAmount: number,
    tma_source: ExpenseSource,
    rowData: QuarterCloseAdjustmentGridData,
    skipEffectedVendorEstimateRows?: boolean
  ): QuarterCloseAdjustmentGridData[] {
    if (!colDefField) {
      return [];
    }

    const precision = colDefField === 'vendor_estimate_percent_ltd' ? 4 : 3;

    const val = isObject(rawNewValue) ? rawNewValue[colDefField] : rawNewValue;

    const newValue = Utils.roundToNumber(val || 0, precision);

    const historicalAdjustmentAmount =
      rowData.historical_adjustment?.adjustment_type ===
        AdjustmentType.ADJUSTMENT_AMOUNT_SYSTEM_GENERATED &&
      colDefField !== 'historical_adjustment_amount'
        ? 0
        : pHistoricalAdjustmentAmount || 0;

    const tmaUnitCost = isNaN(pTmaUnitCost) ? 0 : pTmaUnitCost;

    this.rows.edited.add(activityId);

    let newValues: {
      historical_adjustment_amount: number;
      tma_amount: number;
      tma_unit: number;
      vendor_estimate_amount?: number;
    };
    let vendorEstimateAffectedRows: string[] = [];

    if (colDefField !== 'tma_source') {
      this.manualsToBeDeleted.delete(activityId);
    }

    switch (colDefField) {
      case 'vendor_estimate_percent_ltd':
        {
          this.rows.vendorEstimatePercentChanged.add({ activity_id: activityId, time: dayjs() });
          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: tmaAmount,
            tma_unit_cost: tmaUnitCost,
            vendor_estimate_amount: oldValue,
          });

          const override = this.shouldVendorEstimateOverride({
            activity_id: activityId,
            current_tma_source: tma_source,
            row: rowData,
          });

          const vendorEstimateAmount = (newValue / 100) * totalAmount - rowData.actuals_to_date;

          newValues = {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: override ? vendorEstimateAmount : tmaAmount,
            tma_unit: override ? vendorEstimateAmount / tmaUnitCost : tmaUnit,
            vendor_estimate_amount: vendorEstimateAmount,
          };

          this.vendorEstimateSelectableCategories.add(
            `${rowData.activity_type}_${rowData.organization_id}`
          );

          if (rowData.activity_type === ActivityType.ACTIVITY_SERVICE) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_DISCOUNT}_${rowData.organization_id}`
            );
          }

          if (rowData.activity_type === ActivityType.ACTIVITY_DISCOUNT) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_SERVICE}_${rowData.organization_id}`
            );
          }

          vendorEstimateAffectedRows = this.getActivitiesEffectedByVendor(
            rowData.activity_type,
            rowData.organization_id
          );
        }

        break;
      case 'vendor_estimate_amount_ltd':
        {
          this.rows.vendorEstimateAmountLtdChanged.add({ activity_id: activityId, time: dayjs() });
          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: tmaAmount,
            tma_unit_cost: tmaUnitCost,
            vendor_estimate_amount: oldValue,
          });

          const override = this.shouldVendorEstimateOverride({
            activity_id: activityId,
            current_tma_source: tma_source,
            row: rowData,
          });

          const vendorEstimateAmount = newValue - rowData.actuals_to_date;

          newValues = {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: override ? vendorEstimateAmount : tmaAmount,
            tma_unit: override ? (tmaUnitCost ? vendorEstimateAmount / tmaUnitCost : 0) : tmaUnit,
            vendor_estimate_amount: vendorEstimateAmount,
          };

          this.vendorEstimateSelectableCategories.add(
            `${rowData.activity_type}_${rowData.organization_id}`
          );

          if (rowData.activity_type === ActivityType.ACTIVITY_SERVICE) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_DISCOUNT}_${rowData.organization_id}`
            );
          }

          if (rowData.activity_type === ActivityType.ACTIVITY_DISCOUNT) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_SERVICE}_${rowData.organization_id}`
            );
          }

          vendorEstimateAffectedRows = this.getActivitiesEffectedByVendor(
            rowData.activity_type,
            rowData.organization_id
          );
        }

        break;
      case 'vendor_estimate_amount':
        {
          this.rows.vendorEstimateChanged.add({ activity_id: activityId, time: dayjs() });
          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: tmaAmount,
            tma_unit_cost: tmaUnitCost,
            vendor_estimate_amount: oldValue,
          });

          const override = this.shouldVendorEstimateOverride({
            activity_id: activityId,
            current_tma_source: tma_source,
            row: rowData,
          });

          newValues = {
            historical_adjustment_amount: historicalAdjustmentAmount,
            tma_amount: override ? newValue : tmaAmount,
            tma_unit: override ? (tmaUnitCost ? newValue / tmaUnitCost : 0) : tmaUnit,
            vendor_estimate_amount: newValue,
          };

          this.vendorEstimateSelectableCategories.add(
            `${rowData.activity_type}_${rowData.organization_id}`
          );

          if (rowData.activity_type === ActivityType.ACTIVITY_SERVICE) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_DISCOUNT}_${rowData.organization_id}`
            );
          }

          if (rowData.activity_type === ActivityType.ACTIVITY_DISCOUNT) {
            this.vendorEstimateSelectableCategories.add(
              `${ActivityType.ACTIVITY_SERVICE}_${rowData.organization_id}`
            );
          }

          vendorEstimateAffectedRows = this.getActivitiesEffectedByVendor(
            rowData.activity_type,
            rowData.organization_id
          );
        }

        break;
      case 'tma_unit':
        {
          const tma_amount = newValue * tmaUnitCost;
          const historical_adjustment_amount = historicalAdjustmentAmount;

          this.rows.unitChanged.add(activityId);

          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount,
            tma_amount: tmaUnitCost * oldValue,
            tma_unit_cost: tmaUnitCost,
            vendor_estimate_amount,
          });
          newValues = {
            historical_adjustment_amount,
            tma_amount,
            tma_unit: newValue,
            vendor_estimate_amount,
          };
        }

        break;
      case 'tma_amount':
        {
          const historical_adjustment_amount = historicalAdjustmentAmount;
          const tma_unit = newValue / tmaUnitCost || 0;

          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount,
            tma_amount: oldValue,
            tma_unit_cost: oldValue / tmaUnit,
            vendor_estimate_amount,
          });
          newValues = {
            historical_adjustment_amount,
            tma_amount: newValue,
            tma_unit,
            vendor_estimate_amount,
          };

          this.rows.totalChanged.add(activityId);
        }

        break;
      case 'historical_adjustment_amount':
        {
          rowData.notes = [];
          this.rows.historicalAdjustmentChanged.add(activityId);
          this.editedOldGridRow.set(activityId, {
            historical_adjustment_amount: oldValue,
            tma_amount: tmaAmount,
            tma_unit_cost: tmaUnitCost,
            vendor_estimate_amount,
          });
          newValues = {
            historical_adjustment_amount: +historicalAdjustmentAmount,
            tma_amount: tmaAmount,
            tma_unit: tmaUnit,
            vendor_estimate_amount,
          };
        }

        break;
      case 'tma_source':
        {
          this.rows.unitChanged.delete(activityId);
          this.rows.totalChanged.delete(activityId);

          this.rows.sourceChanged.add(activityId);

          if ((oldValue as unknown as string) === ExpenseSource.Manual) {
            this.manualsToBeDeleted.add(activityId);
          }

          const isAutoGenerated = this.isAutoGeneratedHistoricalAdjustment(
            rowData.historical_adjustment?.adjustment_type
          );

          const historical_adjustment_amount =
            isAutoGenerated && !this.rows.historicalAdjustmentChanged.has(activityId)
              ? 0
              : rowData.historical_adjustment_amount;

          switch (rawNewValue as unknown as ExpenseSource) {
            case ExpenseSource.Forecast:
              newValues = {
                historical_adjustment_amount,
                tma_amount: rowData.current_forecast_amount,
                tma_unit: rowData.current_forecast_unit,
              };
              break;
            case ExpenseSource.Invoice:
              newValues = {
                historical_adjustment_amount,
                tma_amount: rowData.invoices_amount,
                tma_unit: rowData.invoices_amount
                  ? rowData.invoices_amount / rowData.tma_unit_cost
                  : 0,
              };
              break;
            case ExpenseSource.Manual:
              newValues = {
                historical_adjustment_amount,
                tma_amount: rowData.tma_amount,
                tma_unit: rowData.tma_unit,
              };
              break;
            case ExpenseSource['Evidence Based']: {
              // auto historical adjustment
              const auto_historical_adjustment = this.getAutoHistoricalAdjustmentAmount(
                rowData.evidence_based_to_date?.amount || 0,
                rowData.work_performed_to_date?.amount || 0,
                rowData.evidence_based?.amount || 0
              );

              if (
                auto_historical_adjustment !== 0 &&
                isAutoGenerated &&
                !this.rows.historicalAdjustmentChanged.has(activityId)
              ) {
                rowData.notes = [
                  {
                    __typename: 'Note',
                    create_date: dayjs().toISOString(),
                    created_by: EMPTY_UUID,
                    entity_id: rowData.activity_id,
                    entity_type: EntityType.ACTIVITY,
                    expense_note_types: [ExpenseNoteType.EXPENSE_NOTE_HISTORICAL_ADJUSTMENT],
                    id: '',
                    message: 'Auxilius Generated Historical Adjustment for Lagging EDC Data',
                    note_type: NoteType.NOTE_TYPE_EXPENSE,
                  } as Note,
                ];
              }

              newValues = {
                historical_adjustment_amount:
                  isAutoGenerated && !this.rows.historicalAdjustmentChanged.has(activityId)
                    ? auto_historical_adjustment
                    : rowData.historical_adjustment_amount,
                tma_amount: rowData.evidence_based_amount,
                tma_unit: rowData.evidence_based_unit,
              };
              break;
            }

            case ExpenseSource['Vendor Estimate']:
              newValues = {
                historical_adjustment_amount,
                tma_amount: rowData.vendor_estimate_amount,
                tma_unit: rowData.vendor_estimate_unit,
              };
              break;
          }
        }

        break;
      default:
        newValues = {
          historical_adjustment_amount: 0,
          tma_amount: 0,
          tma_unit: 0,
        };
        break;
    }

    const relatedRows = this.gridData$
      .getValue()
      .filter((r) => r.organization_id === rowData.organization_id)
      .filter((r) => r.activity_type === rowData.activity_type);
    const doesSelectedMonthHasVendorEstimate =
      relatedRows.some((row) => row.vendor_estimate !== null) ||
      relatedRows.some((row) =>
        [
          ...this.rows.vendorEstimateChanged,
          ...this.rows.vendorEstimatePercentChanged,
          ...this.rows.vendorEstimateAmountLtdChanged,
        ].some((item) => item.activity_id === row.activity_id)
      ) ||
      colDefField === 'vendor_estimate_amount';

    const isVendorEstimatePercentLTDCol = colDefField === 'vendor_estimate_percent_ltd';

    const isVendorEstimateAmountLTDCol = colDefField === 'vendor_estimate_amount_ltd';

    return this.gridData$
      .getValue()
      .filter(
        (row) =>
          row.activity_id === activityId ||
          (skipEffectedVendorEstimateRows
            ? false
            : vendorEstimateAffectedRows.includes(row.activity_id))
      )
      .map((row) => {
        if (row.activity_id === activityId) {
          const total_monthly_expense =
            newValues.tma_amount + +newValues.historical_adjustment_amount;

          const setting = this.expenseSettings.find((x) => x.activity_id === row.activity_id);

          if (!setting) {
            return row;
          }

          let amount = 0;

          if (!this.rows.sourceChanged.has(row.activity_id)) {
            let amountSet = false;

            setting.sources.forEach((s) => {
              if (amountSet) {
                return;
              }

              switch (s) {
                case ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED:
                  if (row.evidence_based_exist) {
                    amount = row.evidence_based_amount;
                    amountSet = true;
                  }

                  break;
                case ExpenseSourceType.EXPENSE_SOURCE_FORECAST:
                  if (this.costCategoryHasForecasts(row.activity_type, this.gridData$.getValue())) {
                    amount = row.current_forecast_amount;
                    amountSet = true;
                  }

                  break;
                case ExpenseSourceType.EXPENSE_SOURCE_INVOICE:
                  if (row.invoices_amount_exist) {
                    amount = row.invoices_amount;
                    amountSet = true;
                  }

                  break;
                case ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT:
                  break;
                case ExpenseSourceType.EXPENSE_SOURCE_NONE:
                  break;
                case ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE:
                  if (
                    doesSelectedMonthHasVendorEstimate ||
                    isVendorEstimatePercentLTDCol ||
                    isVendorEstimateAmountLTDCol
                  ) {
                    amount =
                      isVendorEstimatePercentLTDCol || isVendorEstimateAmountLTDCol
                        ? newValues.vendor_estimate_amount || 0
                        : row.vendor_estimate_amount;

                    amountSet = true;
                  }

                  break;
              }
            });
          } else {
            switch (tma_source) {
              case ExpenseSource.Forecast:
                amount = row.current_forecast_amount;
                break;
              case ExpenseSource.Invoice:
                amount = row.invoices_amount;
                break;
              case ExpenseSource.Manual:
                amount = row.manual_adjustment?.amount || 0;
                break;
              case ExpenseSource.None:
                break;
              case ExpenseSource['Evidence Based']:
                amount = row.evidence_based_amount;
                break;
              case ExpenseSource['Vendor Estimate']:
                amount = row.vendor_estimate_amount;
                break;
            }
          }

          const previous_tma_amount = amount;

          const override = this.shouldVendorEstimateOverride({
            activity_id: activityId,
            current_tma_source: tma_source,
            row,
          });

          const newTmaSource = ['tma_amount', 'tma_unit'].includes(colDefField)
            ? ExpenseSource.Manual
            : colDefField === 'tma_source'
              ? (rawNewValue as unknown as ExpenseSource)
              : (colDefField === 'vendor_estimate_amount' ||
                    colDefField === 'vendor_estimate_percent_ltd' ||
                    colDefField === 'vendor_estimate_amount_ltd') &&
                  override
                ? ExpenseSource['Vendor Estimate']
                : tma_source;

          if (row.tma_source !== newTmaSource) {
            this.historicalAdjustmentToBeDeleted.add(row.activity_id);
          }

          const isVendorEstimateCell = [
            'vendor_estimate_amount',
            'vendor_estimate_amount_ltd',
            'vendor_estimate_percent_ltd',
          ].includes(colDefField);

          const vendor_estimate_unit =
            isVendorEstimateCell &&
            row.activity_type !== ActivityType.ACTIVITY_DISCOUNT &&
            row.tma_source !== ExpenseSource.Manual
              ? newValues.tma_unit
              : this.getVendorEstimateUnit(
                  newValues.vendor_estimate_amount ?? 0,
                  row.tma_unit_cost
                );

          const vendor_estimate_percentage = isVendorEstimateCell
            ? this.getVendorEstimatePercent(newValues.vendor_estimate_amount ?? 0, totalAmount)
            : row.vendor_estimate_percentage;

          const vendor_estimate_percent_ltd =
            colDefField === 'vendor_estimate_percent_ltd'
              ? newValue
              : this.getVendorEstimatePercentCompleteLTD(
                  newValues.vendor_estimate_amount || vendor_estimate_amount,
                  totalAmount,
                  rowData.actuals_to_date,
                  rowData.prev_months_accruals?.amount || 0
                );

          const vendor_estimate_amount_ltd =
            colDefField === 'vendor_estimate_amount_ltd'
              ? newValue
              : this.getVendorEstimateAmountCompleteLTD(
                  newValues.vendor_estimate_amount || vendor_estimate_amount,
                  rowData.actuals_to_date,
                  rowData.prev_months_accruals?.amount || 0
                );

          if (tma_source !== newTmaSource && colDefField !== 'tma_source') {
            this.rows.sourceChanged.delete(row.activity_id);
          }

          return {
            ...row,
            ...newValues,
            expense_ltd: total_monthly_expense + row.actuals_to_date,
            tma_percentage: totalAmount ? (total_monthly_expense / totalAmount) * 100 : 0,
            tma_source: newTmaSource,
            tma_unit: row.activity_type === ActivityType.ACTIVITY_DISCOUNT ? 0 : newValues.tma_unit,
            total_adjustment: total_monthly_expense - (previous_tma_amount || 0),
            total_monthly_expense,
            variance_to_forecast: decimalDifference(
              newValues.tma_amount,
              row.current_forecast_amount
            ),
            variance_to_previous_month: total_monthly_expense - row.prev_month_amount,
            variance_to_previous_month_percentage: this.calcVarianceToPreviousMonthPercentage(
              total_monthly_expense,
              row.prev_month_amount
            ),
            vendor_estimate_amount_ltd,
            vendor_estimate_percent_ltd,
            vendor_estimate_percentage,
            vendor_estimate_unit,
            vendor_estimate_unit_cost: row.direct_cost.unit_cost,
          };
        } else if (
          vendorEstimateAffectedRows.includes(row.activity_id) &&
          !skipEffectedVendorEstimateRows
        ) {
          if (this.shouldOverrideSourceToVendorEstimate(row)) {
            return this.getVendorEstimateAffectedRow(row);
          }
        }

        return row;
      });
  }

  private calcVarianceToPreviousMonthPercentage(
    total_monthly_expense: number,
    prev_month_amount: number
  ): null | number {
    return prev_month_amount === 0
      ? null
      : (total_monthly_expense - prev_month_amount) / Math.abs(prev_month_amount);
  }

  async canDeactivate(): Promise<boolean> {
    if (
      this.saveCheck$.getValue() &&
      this.launchDarklyService.$select((flags) => flags.tab_in_month_adjustments)()
    ) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  checkIfAllEditableColumnsAreHidden() {
    const config = new ColumnChooserService(this.gridApi$.value!).getTypeSafeColumnChooserConfig(
      this.columnChooserConfigName(),
      this.excludeColumnChooserColumns()
    );

    this.allEditableColumnsHidden$.next(!!this.requiredColumnsSelectionValidation(config));
  }

  private checkTimelineValidity(vendorId: string) {
    const timelines = this.timelines$.value;
    const selectedMonth = dayjs(this.selected_month.value).startOf('month').format('YYYY-MM-DD');

    const timeline = timelines.find(
      (t) => t.vendor_id === vendorId && t.timeline_month === selectedMonth
    );

    const nonvendor_timeline = timelines.find((t) => {
      return t.vendor_id === null && t.timeline_month === selectedMonth;
    });

    return (
      (timeline &&
        (timeline.month_close_source === EventType.BUDGET_MONTH_CLOSED ||
          timeline.month_close_source === EventType.BUDGET_TEMPLATE_UPLOADED)) ||
      (nonvendor_timeline &&
        (nonvendor_timeline.month_close_state === 'OPEN' ||
          nonvendor_timeline.month_close_state === 'FUTURE' ||
          nonvendor_timeline.month_close_state === 'CLOSED'))
    );
  }

  private clearRouterQueryParams() {
    this.router
      .navigate([], {
        queryParams: {
          currentOpenMonth: null,
          editMode: null,
          vendorId: null,
        },
        queryParamsHandling: 'merge',
      })
      .catch(console.error);
  }

  constructor() {
    const mapColKeyAndFeatureFlag = new Map<string, string>([
      ['showAvailableInvoice', 'invoices_available'],
      ['showUOM', 'uom'],
      ['showVendorEstimateAmount', 'vendor_estimate_amount_ltd'],
      ['showVendorEstimatePercent', 'vendor_estimate_percent_ltd'],
    ]);

    effect(() => {
      Object.entries(this.flags()).forEach(([key, value]) => {
        const colKey = mapColKeyAndFeatureFlag.get(key);

        if (!colKey || !this.gridApi()) {
          return;
        }

        this.excludeColumnChooserColumns.update((prev) => {
          if (value) {
            return prev.filter((col) => col !== colKey);
          }

          return uniq([...prev, colKey]);
        });
      });
    });

    this.trialSettingsService
      .getAutoHistoricalAdjustmentSetting$()
      .pipe(takeUntilDestroyed())
      .subscribe(
        (settingValue) =>
          (this.isAutoHistoricalAdjustmentsEnabled =
            settingValue === this.trialSettingsService.autoHistoricalAdjustmentBoxChecked)
      );

    this.invoiceService.initialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // If multiple vendors are not active, and the selected vendor has more than one value,
    // set the selected vendor to the first value
    effect(() => {
      if (!this.multipleVendorsActive()) {
        const firstVendor = _first(
          this.organizationQuery
            .allVendors()
            .sort((a, b) => Utils.alphaNumSort(a.name ?? '', b.name ?? ''))
        );

        if (firstVendor) {
          this.setSelectedVendor([firstVendor.id]);
        }
      }
    });

    this.gridData$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.onFilterChanged();
    });

    this.saveCheck$.pipe(takeUntilDestroyed(), distinctUntilChanged()).subscribe((val) => {
      this.closingPageStore.update({ adjustmentHasUnsavedChanges: val });
    });

    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          this.timelines$.next([]);
          this.timelineLoading.set(true);

          return this.gqlService.listTrialVendorTimeline$();
        }),
        takeUntilDestroyed()
      )
      .subscribe(({ data }) => {
        if (data) {
          this.timelines$.next(data);
        }

        this.timelineLoading.set(false);
      });

    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS],
        sysAdminsOnly: false,
      }),
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_EDIT_VENDOR_ESTIMATE],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([adjustmenPerm, venderEPerm]) => {
        this.userHasAdjustPermission$.next(adjustmenPerm);
        this.hasEditVendorEstimatePermission$.next(venderEPerm);
      });

    combineLatest([
      this.quarterCloseAdjustmentsService.selectedMonthValue$,
      this.quarterCloseAdjustmentsService.selectedVendorValue$,
      this.quarterCloseAdjustmentsService.selectedCategoryValue$,
    ])
      .pipe(
        map(([selectedMonth, selectedVendor]) => {
          if (selectedMonth) {
            if (this.months.find((x) => x.iso === selectedMonth)) {
              this.selected_month.setValue(selectedMonth);
            }
          }

          if (selectedVendor.length) {
            this.setSelectedVendor(selectedVendor);
          }
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    combineLatest([
      this.isWorkflowLocked$,
      this.isSelectedMonthOpen$,
      this.isSelectedCategoryDiscount$,
      this.userHasAdjustPermission$,
      this.hasEditVendorEstimatePermission$,
      this.isVendorEstimatesLocked$,
      this.allEditableColumnsHidden$,
    ])
      .pipe(
        map(
          ([
            isWorkflowLocked,
            isSelectedMonthOpen,
            isSelectedCategoryDiscount,
            adjustPerm,
            editVEPerm,
            isVendorEstimatesLocked,
            allEditableColumnsHidden,
          ]) => {
            const disabled =
              isWorkflowLocked ||
              !isSelectedMonthOpen ||
              isSelectedCategoryDiscount ||
              (!adjustPerm && editVEPerm && isVendorEstimatesLocked);

            this.editButtonDisabled$.next(disabled);

            const reasonsToDisable = [];

            if (!isSelectedMonthOpen) {
              reasonsToDisable.push(MessagesConstants.CHANGES_UNABLE_SINCE_MONTH_CLOSED);
            } else {
              if (isSelectedCategoryDiscount) {
                reasonsToDisable.push(MessagesConstants.CANT_ADJUST_DISCOUNT);
              }

              if (isWorkflowLocked) {
                reasonsToDisable.push(MessagesConstants.VENDOR_EXPENSES_LOCKED);
              }

              if (allEditableColumnsHidden) {
                const { tmaCanBeEdited, veCanBeEdited } = this.possibleEditColumns();

                if (!editVEPerm && !veCanBeEdited) {
                  reasonsToDisable.push(
                    'You do not have permission to edit Vendor Estimate. Please contact your system administrator if this has been restricted by mistake.'
                  );
                }

                if (isVendorEstimatesLocked && !veCanBeEdited) {
                  reasonsToDisable.push(MessagesConstants.VENDOR_ESTIMATES_LOCKED);
                }

                if (!adjustPerm && !tmaCanBeEdited) {
                  reasonsToDisable.push(
                    'You do not have permission to edit Total Month Expenses. Please contact your system administrator if this has been restricted by mistake.'
                  );
                }

                reasonsToDisable.push(MessagesConstants.NO_EDITABLE_COLUMNS_SELECTED_MESSAGE);
              }
            }

            this.editButtonReasonsToDisabled.set(reasonsToDisable);
          }
        ),
        takeUntilDestroyed()
      )
      .subscribe();
    this.isWorkflowLocked$.pipe(takeUntilDestroyed()).subscribe((bool) => {
      if (bool && this.editMode$.getValue()) {
        this.onCancel();
      }
    });
    combineLatest([this.selected_category.valueChanges, this.sitesService.get(), this.gridApi$])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (
          this.selected_category.value === ActivityType.ACTIVITY_DISCOUNT &&
          this.editMode$.getValue()
        ) {
          this.onCancel();
        }

        this.updateCategoryFiltering();
      });

    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((users) => {
        users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });

    this.selected_vendor.valueChanges
      .pipe(distinctUntilChanged(isEqual), takeUntilDestroyed())
      .subscribe(() => {
        if (this.selectedVendors.length) {
          this.quarterCloseAdjustmentsService.updateFormControlValues('', this.selectedVendors);
        }
      });

    this.selected_category.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        this.quarterCloseAdjustmentsService.updateSelectCategory(
          this.selected_category.value || ''
        );
      });

    this.selected_threshold.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.gridApi$.value?.onFilterChanged();
    });

    this.search.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(), debounceTime(1000))
      .subscribe(() => {
        this.onFilterChanged();
      });

    combineLatest([
      this.selected_vendor.valueChanges.pipe(
        startWith(this.selectedVendors),
        distinctUntilChanged(isEqual)
      ),
      this.vendorEstimateSupportingDocUploaded$.pipe(startWith(null)),
      this.eventService.select$(EventType.NEW_TASK).pipe(startWith(null)),
    ])
      .pipe(
        switchMap(() => {
          if (this.selectedVendors.length) {
            const filterModel = {
              document_type_id: {
                filter: DocumentType.DOCUMENT_VENDOR_ESTIMATE_SUPPORT,
                filterType: 'text',
                type: 'equals',
              },
              vendor_id: {
                filterType: 'set',
                values: this.selectedVendors,
              },
            };

            return from(this.apiService.getDocumentLibraryList(JSON.stringify(filterModel)));
          }

          return of([]);
        }),
        tap((data: listDocumentsQuery['items']) => {
          this.vendorEstimateSupportingDoc$.next(data || []);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    combineLatest([
      this.selected_month.valueChanges.pipe(
        startWith(this.selected_month.value),
        distinctUntilChanged()
      ),
    ])
      .pipe(
        switchMap(([selected_month]) => {
          this.vendorEstimatesLoading.set(true);

          if (selected_month && !this.router.url.includes(ROUTING_PATH.CLOSING.CHECKLIST)) {
            this.quarterCloseAdjustmentsService.updateFormControlValues(selected_month);

            return from(
              this.gqlService.listVendorEstimateSummaries$(dayjs(selected_month).format('MMM-YYYY'))
            );
          }

          return of({ data: [] });
        }),
        tap(({ data }) => {
          this.vendorEstimates$.next(data || []);
          this.vendorEstimatesLoading.set(false);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    combineLatest([
      this.selected_month.valueChanges.pipe(
        startWith(this.selected_month.value),
        distinctUntilChanged()
      ),
      this.selected_vendor.valueChanges.pipe(
        startWith(this.selectedVendors),
        distinctUntilChanged(isEqual)
      ),
    ])
      .pipe(
        distinctUntilChanged(isEqual),
        switchMap(([month]) => {
          const vendors = this.selectedVendors;

          this.showCantEdit = false;

          if (this.editMode$.getValue()) {
            this.onCancel();
          }

          this.gridDataLoading.set(true);

          if (month) {
            // Persist selected month to sync with quarter-close-checklist
            this.periodCloseService.persistedQuarterMonth = month;
            this.periodCloseService.selectedQuarterMonthChanged$.next(null);

            const formattedMonth = dayjs(month).format('MMM-YYYY').toUpperCase();

            return this.timelineLoading$.pipe(
              filterRxjs((x) => !x),
              switchMap(() => {
                if (this.selectedVendors.length === 1 && !this.checkTimelineValidity(vendors[0])) {
                  this.showCantEdit = true;

                  this.expenseSettings = [];
                  this.inMonthExpenses$.next([]);
                  this.invoiceCategoryTotalsByVendor = new Map();
                  this.discountExpenses$.next([]);
                  this.parseExpenses();
                  this.gridDataLoading.set(false);

                  return EMPTY;
                }

                const vendorsInTimeline = this.selectedVendors.filter((v) =>
                  this.checkTimelineValidity(v)
                );

                return combineLatest([
                  this.monthExpensesService.fetchMonthExpenses$(
                    vendorsInTimeline ? vendorsInTimeline : null,
                    formattedMonth
                  ),
                  this.gqlService.listDiscountExpenses$({
                    amount_types: [AmountType.AMOUNT_DISCOUNT],
                    organization_ids: vendorsInTimeline ? vendorsInTimeline : null,
                    period: formattedMonth,
                  }),
                  this.gqlService.listExpenseSourceSettings$({
                    organization_ids: vendorsInTimeline ? vendorsInTimeline : null,
                    period: dayjs(month).format('YYYY-MM-DD'),
                  }),
                  this.gqlService.listInvoiceCategoryTotals$({
                    period: formattedMonth,
                    vendor_ids: vendorsInTimeline ? vendorsInTimeline : [],
                  }),
                ]);
              })
            );
          }

          this.gridDataLoading.set(false);

          return EMPTY;
        }),
        takeUntilDestroyed()
      )
      .subscribe(([{ data, errors }, discountExpenses, expenseSettings, invoiceCategoryTotals]) => {
        const err = errors || discountExpenses.errors;

        if (err) {
          this.overlayService.error(err);
        }

        this.expenseSettings = expenseSettings.data || [];
        this.isSelectedMonthOpen$.next(
          dayjs(this.periodCloseComponent.currentMonth)
            .date(1)
            .isSame(dayjs(this.selected_month.value).date(1))
        );
        this.isSelectedMonthFuture = dayjs(this.periodCloseComponent.currentMonth)
          .date(1)
          .isBefore(dayjs(this.selected_month.value).date(1));
        this.isSelectedMonthOpenOrFuture$.next(
          dayjs(this.periodCloseComponent.currentMonth)
            .date(1)
            .isSameOrBefore(dayjs(this.selected_month.value).date(1))
        );

        if (invoiceCategoryTotals.success && invoiceCategoryTotals.data) {
          this.invoiceCategoryTotalsByVendor = new Map(
            invoiceCategoryTotals.data.map((totals) => [totals.organization_id, totals])
          );
        }

        this.inMonthExpenses$.next(data || []);
        this.discountExpenses$.next(discountExpenses.data || []);
        this.parseExpenses();

        this.gridDataLoading.set(false);
      });

    combineLatest([
      this.shouldShowEvidenceColumns$,
      this.vendorEstimates$,
      toObservable(this.trialAttributesLoading),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.gridUpdated = false;
        this.gridOptions$.next(this.getGridOptions());
        setTimeout(() => {
          this.gridUpdated = true;
          this.cdr.detectChanges();
        }, 0);
      });

    combineLatest([
      this.periodCloseComponent.quartersObjUpdated$.pipe(startWith(null)),
      this.periodCloseComponent.selectedQuarter.valueChanges.pipe(
        startWith(this.periodCloseComponent.selectedQuarter.value)
      ),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateMonths();
      });
    this.gridData$.pipe(takeUntilDestroyed()).subscribe((gd) => {
      if (gd.length > 0) {
        const usedActivityTypes: ActivityType[] = [];

        gd.forEach((element) => {
          if (!usedActivityTypes.includes(element.activity_type)) {
            usedActivityTypes.push(element.activity_type);
          }
        });
        this.categories = this.defaultCategories.filter(
          (category) =>
            category.value === '' || usedActivityTypes.includes(category?.value as ActivityType)
        );

        //reset selected category to all if vendor does not have that category
        if (!this.categories.some((category) => category.value === this.selected_category.value)) {
          this.selected_category.setValue(this.defaultCategories[0].value);
        }
      }
    });
  }

  costCategoryHasEvidenceBased(
    activity_type: ActivityType,
    rows: QuarterCloseAdjustmentGridData[]
  ) {
    return rows
      .filter((row) => row.activity_type === activity_type)
      .map((row) => row.evidence_based_exist)
      .reduce((acc, currentRow) => acc || currentRow, false);
  }

  costCategoryHasForecasts(activity_type: ActivityType, rows: QuarterCloseAdjustmentGridData[]) {
    return rows
      .filter((row) => row.activity_type === activity_type)
      .map((row) => row.is_forecasted)
      .reduce((acc, currentRow) => acc || currentRow, false);
  }

  costCategoryHasForecastSelected = (params: ICellEditorParams | ValueSetterParams) => {
    const activity_type = params.data.activity_type as string;
    const vendorId = params.data.organization_id as string;

    // Get all same activity type as the same activity type as row
    const allActivitiesSameType = this.gridData$
      .getValue()
      .filter((row) => row.activity_type === activity_type && row.organization_id === vendorId);

    // If there is an activity that already forecast selected before return true else false
    const hasForecastSelected = allActivitiesSameType.some((activity) => !!activity.is_forecasted);

    // Get a unique list of activity ids
    return hasForecastSelected;
  };

  doesAllVendorsHaveSameCurrency(): boolean {
    const selectedVendors = this.selectedVendors.map((id) => this.organizationQuery.getEntity(id));

    if (!selectedVendors.length) {
      return true;
    }

    const firstVendor = selectedVendors[0];

    return selectedVendors.every(
      (vendor) => vendor?.currency === (firstVendor?.currency || Currency.USD)
    );
  }

  // Need to bind context, ag-grid lose controller context after passing it to the component
  doesExternalFilterPass = (node: IRowNode<QuarterCloseAdjustmentGridData>) => {
    if (!node?.data) {
      return true;
    }

    // Check over budget filter
    if (this.overBudgetFilterValue()) {
      const expenseLtd = Math.abs(node.data.expense_ltd || 0);
      const totalAmount = Math.abs(node.data.total_amount || 0);

      if (expenseLtd <= totalAmount) {
        return false;
      }
    }

    // Check threshold filter
    if (!this?.selected_threshold?.value) {
      return true;
    }

    return (
      node.data.current_forecast_amount > +this.selected_threshold.value || // Current Forecast Monthly Expense
      node.data.vendor_estimate_amount > +this.selected_threshold.value || // Vendor Estimate Monthly Expense
      node.data.invoices_amount > +this.selected_threshold.value || // Invoice Monthly Expense
      node.data.evidence_based_amount > +this.selected_threshold.value || // Evidence Based Monthly Expense
      node.data.tma_amount > +this.selected_threshold.value || //  Monthly Expense
      node.data.total_monthly_expense > +this.selected_threshold.value // Monthly Expense  incl.  Historical Adjustments
    );
  };

  doesInvoiceMappingsMatchTotal = (params: ICellRendererParams): boolean => {
    if (
      params.value &&
      !!params.node.aggData &&
      ['cost_category_name', 'vendorName'].includes(params.node?.field ?? '')
    ) {
      return params.value === params.node.aggData.invoices_amount;
    }

    return false;
  };

  generatePinnedBottomData(gridData: QuarterCloseAdjustmentGridData[]) {
    if (!this.doesAllVendorsHaveSameCurrency()) {
      this.gridApi$.getValue()?.setGridOption('pinnedBottomRowData', []);
      this.bottomRowData$.next(null);

      return;
    }

    const selectedCategory = this.selected_category.value;
    const columns = [
      'total_amount',
      'actuals_to_date',
      'total_remaining',
      'prev_month_amount',
      'current_forecast_amount',
      'vendor_estimate_amount',
      'evidence_based_amount',
      'tma_amount',
      'variance_to_forecast',
      'historical_adjustment_amount',
      'actuals_performed_to_date',
      'total_adjustment',
      'total_monthly_expense',
      'expense_ltd',
      'previous_months_accruals',
      'invoices_amount',
      'variance_to_previous_month',
    ] as CalculableColumns[];

    if (this.flags().showUnitTotals) {
      columns.push('units');
      columns.push('tma_unit');
      columns.push('prev_month_unit');
      columns.push('current_forecast_unit');
      columns.push('vendor_estimate_unit');
      columns.push('evidence_based_unit');
    }

    const totals = gridData
      .filter((row) => (selectedCategory ? row.activity_type === selectedCategory : true))
      .reduce(
        (acc, rowValue) => {
          for (const col of columns) {
            acc[col] = Utils.roundToNumber(acc[col] || 0) + Utils.roundToNumber(rowValue[col] || 0);
          }

          return acc;
        },
        {} as Record<CalculableColumns, number>
      );

    totals.invoices_available = 0;

    this.gridApi$.value?.forEachNodeAfterFilter((node) => {
      const aggregateLvlToSum =
        this.selectedVendors.length > 1 ? 'vendorName' : 'cost_category_name';

      if (node?.aggData?.invoices_available && node.field === aggregateLvlToSum) {
        totals.invoices_available += node.aggData.invoices_available;
      }
    });

    if (this.launchDarklyService.flags$.getValue().vendor_estimate_percent_ltd) {
      totals.vendor_estimate_percent_ltd = this.getVendorEstimatePercentCompleteLTD(
        totals.vendor_estimate_amount,
        totals.total_amount,
        totals.actuals_performed_to_date,
        totals.previous_months_accruals
      );
    }

    totals.vendor_estimate_amount_ltd = this.getVendorEstimateAmountCompleteLTD(
      totals.vendor_estimate_amount,
      totals.actuals_performed_to_date,
      totals.previous_months_accruals
    );

    totals.current_forecast_percentage = totals.total_amount
      ? decimalMultiply(decimalDivide(totals.current_forecast_amount, totals.total_amount), 100, 6)
      : 0;
    totals.evidence_based_percentage = totals.total_amount
      ? decimalMultiply(decimalDivide(totals.evidence_based_amount, totals.total_amount), 100, 6)
      : 0;

    this.bottomRowData$.next({
      ...totals,
      variance_to_previous_month_percentage: this.calcVarianceToPreviousMonthPercentage(
        totals.total_monthly_expense,
        totals.prev_month_amount
      ),
      vendorCurrency: gridData[0]?.vendorCurrency || Currency.USD,
    });
    this.updateBottomData();
  }

  getActivitiesEffectedByVendor(activityType: ActivityType, vendorId: string): string[] {
    const activities = (
      this.groupedActivitiesByActivityTypeAndVendor.get(`${activityType}_${vendorId}`) || []
    ).map((row) => row.activity_id);

    if (activityType === ActivityType.ACTIVITY_SERVICE) {
      const discountActivities = this.groupedActivitiesByActivityTypeAndVendor.get(
        `${ActivityType.ACTIVITY_DISCOUNT}_${vendorId}`
      );

      return [...activities, ...(discountActivities || []).map((row) => row!.activity_id)];
    }

    if (activityType === ActivityType.ACTIVITY_DISCOUNT) {
      const serviceActivities = this.groupedActivitiesByActivityTypeAndVendor.get(
        `${ActivityType.ACTIVITY_SERVICE}_${vendorId}`
      );

      return [...activities, ...(serviceActivities || []).map((row) => row!.activity_id)];
    }

    return activities;
  }

  getActivitiesTypes(activityIds: Set<string>) {
    const rows = this.inMonthExpenses$.getValue() || [];

    const filteredRows = filter(rows, (row) => includes(Array.from(activityIds), row.activity_id));

    return uniq(_map(filteredRows, 'activity_type'));
  }

  getAutoHistoricalAdjustmentAmount(
    evidenceBasedToDate: number,
    workPerformedToDate: number,
    evidenceBased: number
  ) {
    return decimalDifference(
      decimalDifference(evidenceBasedToDate, workPerformedToDate),
      evidenceBased
    );
  }

  private getBudgetInfo(vendorId: string) {
    const budget = this.budgetQuery.getValue();

    return budget.budget_info.find((info) => info.vendor_id === vendorId);
  }

  private getFormattedSelectedPeriod() {
    return `${dayjs(this.selected_month.value).format('YYYY-MM')}-01`;
  }

  getGridOptions() {
    const ctx: InMonthAdjustmentGridConfigContext = {
      allEditableColumnsHidden: this.allEditableColumnsHidden,
      attributeConfig: {
        attributes: getTrialAttributes(
          this.globalStore.attributes(),
          this.globalStore.primaryAttribute().attribute_name,
          this.globalStore.secondaryAttribute().attribute_name
        ),
        gridData: this.gridData$.getValue(),
        primaryAttribute: this.globalStore.primaryAttribute(),
        secondaryAttribute: this.globalStore.secondaryAttribute(),
      },
      editableVeColumns: this.editableVeColumns,
      editMode: this.editMode,
      flags: this.flags(),
      gridData: this.gridData$.getValue(),
      permissions: this.permissions(),
      possibleEditColumns: this.possibleEditColumns,
      qcaComponent: this,
      rows: this.rows,
      selectedMonth: this.selected_month.value!,
      updateDynamicFields: this.updateDynamicFields,
    };

    return inMonthAdjustmentGridConfig(ctx);
  }

  getInvoicesAvailableNumber(vendorId: string, activityType: ActivityType) {
    if (activityType === ActivityType.ACTIVITY_SERVICE) {
      return this.invoiceCategoryTotalsByVendor.get(vendorId)?.service_total;
    }

    if (activityType === ActivityType.ACTIVITY_DISCOUNT) {
      return this.invoiceCategoryTotalsByVendor.get(vendorId)?.discount_total;
    }

    if (activityType === ActivityType.ACTIVITY_PASSTHROUGH) {
      return this.invoiceCategoryTotalsByVendor.get(vendorId)?.passthrough_total;
    }

    if (activityType === ActivityType.ACTIVITY_INVESTIGATOR) {
      return this.invoiceCategoryTotalsByVendor.get(vendorId)?.investigator_total;
    }

    return 0;
  }

  getVendorEstimateAffectedRow(row: QuarterCloseAdjustmentGridData) {
    const isAutoGenerated = this.isAutoGeneratedHistoricalAdjustment(
      row.historical_adjustment?.adjustment_type
    );

    return {
      ...row,
      expense_ltd: row.actuals_to_date,
      historical_adjustment_amount: isAutoGenerated ? 0 : row.historical_adjustment_amount,
      tma_amount: 0,
      tma_percentage: 0,
      tma_source: ExpenseSource['Vendor Estimate'],
      tma_unit: 0,
      total_adjustment: isAutoGenerated ? 0 : row.total_adjustment,
      total_monthly_expense: 0,
      variance_to_forecast: -row.current_forecast_amount,
      variance_to_previous_month: 0 - row.prev_month_amount,
      variance_to_previous_month_percentage: this.calcVarianceToPreviousMonthPercentage(
        0,
        row.prev_month_amount
      ),
    };
  }

  private getVendorEstimateAmountCompleteLTD(
    vendorEstimateAmount: number,
    actualsToDate: number,
    prevMonthsAccruals: number
  ): number {
    return decimalAdd(
      decimalAdd(actualsToDate || 0, vendorEstimateAmount || 0),
      prevMonthsAccruals || 0
    );
  }

  private getVendorEstimatePercent(vendorEstimateAmount: number, currentLRE: number) {
    if (!currentLRE) {
      return 0;
    }

    return decimalMultiply(decimalDivide(vendorEstimateAmount || 0, currentLRE), 100);
  }

  private getVendorEstimatePercentCompleteLTD(
    vendorEstimateAmount: number,
    currentLRE: number,
    actualsToDate: number,
    prevMonthsAccruals: number
  ): number {
    if (!currentLRE) {
      return 0;
    }

    return decimalMultiply(
      decimalDivide(
        decimalAdd(
          decimalAdd(actualsToDate || 0, vendorEstimateAmount || 0),
          prevMonthsAccruals || 0
        ),
        currentLRE
      ),
      100,
      6
    );
  }

  private getVendorEstimateUnit(vendorEstimateAmount: number, tmaUnitCost: number) {
    if (!tmaUnitCost) {
      return 0;
    }

    return decimalDivide(vendorEstimateAmount || 0, tmaUnitCost);
  }

  private async goToEditModeByRouteParams() {
    const { currentOpenMonth, editMode, vendorId } = await firstValueFrom(this.route.queryParams);
    const isWorkflowLocked = await firstValueFrom(this.isWorkflowLocked$);

    if (
      editMode &&
      this.isSelectedMonthOpen$.getValue() &&
      !isWorkflowLocked &&
      !this.isVendorEstimatesLocked$.value
    ) {
      setTimeout(() => {
        this.onEditMode();
      }, 500);
    }

    if (vendorId || currentOpenMonth || editMode) {
      this.clearRouterQueryParams();
    }
  }

  // Event handlers for summary accordions
  handleExpenseSourceRowChecked(event: { checked: boolean; rowId: string }): void {
    const { checked, rowId } = event;

    // Apply the filter to the grid - this will trigger onFilterChanged which syncs the values
    this.applyExpenseSourceFilter(rowId, checked);
  }

  handleNotesRowChecked(event: { checked: boolean; rowId: string }): void {
    const { checked, rowId } = event;

    // Apply the filter to the grid - this will trigger onFilterChanged which syncs the values
    this.applyNotesFilter(rowId, checked);
  }

  handleOverBudgetChecked(checked: boolean): void {
    // Apply the filter to the grid - this will trigger onFilterChanged which syncs the values
    this.applyOverBudgetFilter(checked);
  }

  isAutoGeneratedHistoricalAdjustment(adjustmentType?: AdjustmentType | null) {
    return (
      adjustmentType === AdjustmentType.ADJUSTMENT_AMOUNT_SYSTEM_GENERATED ||
      (!adjustmentType && this.isAutoHistoricalAdjustmentsEnabled)
    );
  }

  isExternalFilterPresent = () => this.overBudgetFilterValue() || !!this.selected_threshold.value;

  isUploadVendorEstimateDisabled(): boolean {
    const allReceivesEstimateVendors = this.organizationQuery.getAllReceivesEstimateVendors() || [];

    return !(
      this.selectedVendors.length > 0 &&
      this.selectedVendors.some((vendorId) =>
        allReceivesEstimateVendors.some((vendor) => vendor.id === vendorId)
      )
    );
  }

  onAfterColumnChooserApplied() {
    // Let ag-grid recalculate the cell classes after column chooser is applied
    const gridOptions = this.getGridOptions() as GridOptions;

    if (gridOptions && gridOptions.columnDefs) {
      applyColumnDefsWithPinState(
        this.gridApi$.value!,
        gridOptions.columnDefs,
        this.columnChooserConfigName(),
        this.excludeColumnChooserColumns()
      );
    }

    this.gridApi()?.refreshCells({ force: true });
    this.checkIfAllEditableColumnsAreHidden();
  }

  async onApplySelection(vendorIDs: string[]) {
    const resp = await this.beforeFilterChanges(this.saveCheck$.value)();

    if (!resp) {
      return;
    }

    this.selected_vendor.setValue(vendorIDs);
    this.quarterCloseAdjustmentsService.updateFormControlValues('', vendorIDs);
    this.selected_category.setValue(this.defaultCategories[0].value);
  }

  onCancel() {
    this.editMode$.next(false);
    this.editMode.set(false);
    this.saveCheck$.next(false);
    this.rows.edited.clear();
    this.editedOldGridRow.clear();
    this.rows.unitChanged.clear();
    this.rows.totalChanged.clear();
    this.rows.sourceChanged.clear();
    this.manualsToBeDeleted.clear();
    setTimeout(() => {
      this.triggerReapplyTableEditStyles();
    }, 0);
    this.rows.historicalAdjustmentChanged.clear();
    this.rows.vendorEstimateChanged.clear();
    this.rows.vendorEstimatePercentChanged.clear();
    this.rows.vendorEstimateAmountLtdChanged.clear();
    this.vendorEstimateSelectableCategories.clear();
    this.parseExpenses();

    // Reset filter values for summary accordions
    this.expenseSourceFilterValues.set(new Set());
    this.notesFilterValues.set(new Set());
    this.overBudgetFilterValue.set(false);
  }

  onCategoryDropdownChange(): void {
    this.selected_category.setValue(this.defaultCategories[0].value);
  }

  onCellValueChanged(e: CellValueChangedEvent<QuarterCloseAdjustmentGridData>) {
    if (
      (Number.isFinite(+e.newValue) || e.column.getColId() === 'tma_source') &&
      e.source !== 'rangeService' &&
      e.source !== 'paste'
    ) {
      this.updateDynamicFields(
        e.newValue,
        e.data.activity_id,
        e.colDef.field,
        e.data.tma_unit_cost,
        e.data.historical_adjustment_amount,
        e.data.vendor_estimate_amount,
        e.oldValue,
        e.data.tma_unit,
        e.data.tma_amount,
        e.data.total_amount, // Current (LRE)
        e.data.tma_source,
        e.data
      );
      // wrap multiple calls into a single task while filling the grid
    } else if (e.source === 'rangeService' || e.source === 'paste') {
      this.imaTaskCalculationManagerService.addRowToCalculate(e);
      this.imaTaskCalculationManagerService.calculateRows(
        (event) =>
          this.calculateRow(
            event.newValue,
            event.data.activity_id,
            event.colDef.field,
            event.data.tma_unit_cost,
            event.data.historical_adjustment_amount,
            event.data.vendor_estimate_amount,
            event.oldValue,
            event.data.tma_unit,
            event.data.tma_amount,
            event.data.total_amount, // Current (LRE)
            event.data.tma_source,
            event.data,
            true // skip overrides
          ),
        this.getActivitiesEffectedByVendor.bind(this),
        (newList, additionalRowsToCalculate) => {
          const affectedRows = this.gridData$.value
            .filter((row) => {
              return (
                additionalRowsToCalculate.has(row.activity_id) &&
                this.shouldOverrideSourceToVendorEstimate(row)
              );
            })
            .map((row) => this.getVendorEstimateAffectedRow(row));

          this.gridApi$.value?.applyTransactionAsync(
            {
              update: [...newList, ...affectedRows],
            },
            () => {
              this.saveCheck$.next(true);

              const updatedGridData: QuarterCloseAdjustmentGridData[] = [];

              this.gridApi$.value?.forEachNode((node) => {
                if (node.data) {
                  updatedGridData.push(node.data);
                }
              });

              this.gridData$.next(updatedGridData);
            }
          );
        }
      );
    }
  }

  onClearFilter() {
    const gridAPI = this.gridApi$.getValue();

    if (gridAPI) {
      const filterModel = gridAPI.getFilterModel();

      if (Object.keys(filterModel).length) {
        gridAPI.setFilterModel({});
      }
    }

    if (this.selected_threshold.value !== '') {
      this.selected_threshold.setValue('');
    }

    if (this.selected_category.value !== '') {
      this.selected_category.setValue('');
    }

    // Reset filter values for summary accordions
    this.expenseSourceFilterValues.set(new Set());
    this.notesFilterValues.set(new Set());
    this.overBudgetFilterValue.set(false);

    gridAPI?.onFilterChanged();
  }

  /**
   * Downloads the vendor estimate for the selected vendor and month.
   *
   * This function supports only a single vendor selection. If multiple vendors are selected,
   * only the first vendor's estimate will be downloaded.
   */
  onDownloadVendorEstimates = async () => {
    const { trialKey } = this.mainQuery.getValue();
    const currentMonth = dayjs(this.selected_month.value).format('MMMM-YYYY');
    const currentVendor = this.selectedVendors?.[0];
    const { data, success } = await this.apiService.getS3ZipFile(
      `trials/${trialKey}/vendors/vendor-estimate/${currentMonth}`,
      currentVendor
    );
    const vendorName = this.organizationQuery.getVendor(currentVendor || '')[0].name;

    if (success && data) {
      const fileName = `${
        this.mainQuery.getSelectedTrial()?.short_name
      }-${vendorName}-${currentMonth}-vendor-estimate`;

      await this.apiService.downloadZipOrFile(data, fileName);
    }
  };

  onEditMode = (skipColumns: string[] = []) => {
    this.expandingRows.set(true);
    this.gridApi$.value?.showLoadingOverlay();
    this.editMode$.next(true);
    this.editMode.set(true);

    const gridApi = this.gridApi$.getValue();

    if (!gridApi) {
      return;
    }

    this.triggerReapplyTableEditStyles();

    setTimeout(() => {
      AgEditFirstRow({
        filterNodes: (nodes) =>
          nodes.filter((n) => !(n.key === 'Discount' || n.parent?.key === 'Discount')),
        gridApi,
        passSpecifiedColumns: (columns) => {
          const hasNotEditVEPermButAdjutPerm =
            !this.hasEditVendorEstimatePermission$.getValue() &&
            this.userHasAdjustPermission$.getValue();

          const columnIdsToPass = ['tma_unit'];

          if (
            hasNotEditVEPermButAdjutPerm ||
            this.isVendorEstimatesLocked$.getValue() ||
            !this.vendorEstimateColumnChecker()
          ) {
            columnIdsToPass.push(...this.editableVeColumns);
          } else {
            columnIdsToPass.push(...skipColumns);
          }

          return columns.filter((col) => columnIdsToPass.includes(col.getColId()));
        },
      });

      this.expandingRows.set(false);
      this.gridApi$.value?.hideOverlay();
    }, 1000);
  };

  async onExport() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    let vendorName = '';

    if (this.selectedVendors.length === 1) {
      const vendor = this.organizationQuery.getEntity(this.selectedVendors[0]);

      vendorName = vendor ? `_${vendor.name}_` : '';
    }

    const category = this.categories.filter((c) => c.value === this.selected_category.value)[0];
    const month = dayjs(this.selected_month.value).format('MMMYY');
    const vendorsInTimeline = this.selectedVendors.filter((v) => this.checkTimelineValidity(v));

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.ORGANIZATION,
        payload: JSON.stringify({
          export_type: ExportType.IN_MONTH_ADJUSTMENTS,
          filename: `${trialName}_${month}${vendorName}${category.label}_Adjustments.xlsx`,
          json_properties: {
            activity_ids: agGridAllVisibleData<QuarterCloseAdjustmentGridData>(
              this.gridApi$.value!
            ).map((item) => item.activity_id),
            activity_type: category.value || null,
            materiality_threshold: this.selected_threshold.value,
            month: dayjs(this.selected_month.value).format('MMM-YYYY'),
            organization_ids: vendorsInTimeline,
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  onFilterChanged() {
    const filteredRows: QuarterCloseAdjustmentGridData[] = [];

    this.gridApi$.value?.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredRows.push(node.data);
      }
    });
    this.generatePinnedBottomData(filteredRows);

    // Sync filter values with grid state
    this.syncFilterValuesWithGrid();

    // Trigger analytic cards recalculation
    this.filterChangeTrigger.update((trigger) => trigger + 1);
  }

  onFirstDataRendered() {
    // Initialize filter values based on the actual grid state when data is first rendered
    this.syncFilterValuesWithGrid();
  }

  async onGridReady({ api }: GridReadyEvent) {
    this.gridApi$.next(api);

    if (this.afterOnSave.getValue()) {
      api.forEachNode((node) => {
        node.expanded = true;
      });
      api.onGroupExpandedOrCollapsed();
      this.gridApi$.next(api);
      this.afterOnSave.next(false);
    }

    this.setTableHeight();

    this.gridApi$.value?.setGridOption('rowData', this.gridData$.value);

    await this.goToEditModeByRouteParams();

    this.updateCategoryFiltering();
  }

  async onSave() {
    const api = this.gridApi$.getValue();
    const activity_by_vendor: Record<string, string[]> = {};

    this.afterOnSave.next(true);

    api?.forEachNode((row) => {
      if (row.data && this.rows.edited.has(row.data.activity_id)) {
        if (!activity_by_vendor[row.data.organization_id]) {
          activity_by_vendor[row.data.organization_id] = [row.data.activity_id];
        } else {
          activity_by_vendor[row.data.organization_id].push(row.data.activity_id);
        }
      }
    });

    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        {
          activity_by_vendor: Record<string, string[]>;
          current_month: Maybe<string>;
        },
        AdjustmentModalResponseType,
        AdjustmentModalComponent
      >({
        content: AdjustmentModalComponent,
        data: {
          activity_by_vendor,
          current_month: this.getFormattedSelectedPeriod(),
        },
        settings: {
          header: 'Save Expense Detail',
          primaryButton: {
            action: (instance) => {
              instance?.close(true);
            },
            pendoTag: 'save-expense-detail',
          },
        },
      }).afterClosed$
    );

    if (!resp.data?.note || !api) {
      return;
    }

    const adjustmentData: BudgetExpenseInput[] = [];

    const adjustments: Record<string, listInMonthExpensesQuery['manual_adjustment']> = {};
    const vendorEstimateAdjustments: Record<string, listInMonthExpensesQuery['vendor_estimate']> =
      {};
    const historicalAdjustments: Record<string, listInMonthExpensesQuery['historical_adjustment']> =
      {};

    const { deletedManuals, overriddenSourceRows } = this.expenseSourceService.getSourceChangedRows(
      api,
      this.rows.sourceChanged,
      this.manualsToBeDeleted,
      expenseSourceReverseMapping
    );

    api.forEachNode((row) => {
      if (!row.data) {
        return;
      }

      const isEdited = this.rows.edited.has(row.data.activity_id);
      const isInManualsToBeDeleted = this.manualsToBeDeleted.has(row.data.activity_id);

      if (!isEdited) {
        return;
      }

      if (isInManualsToBeDeleted) {
        const sourceChangedRow = overriddenSourceRows.find((sr) => sr.id === row.data.activity_id);
        const isNewSourceVendorEstimate =
          sourceChangedRow?.newValue === ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE;

        // if the row is edited and then the source is changed to vendor estimate, we need to send batchCreateBudgetExpenses
        // otherwise, we don't need to send anything.
        if (!isNewSourceVendorEstimate) {
          return;
        }
      }

      const budget_info = this.getBudgetInfo(row.data.organization_id);

      const oldData: {
        historical_adjustment_amount: number;
        tma_amount: number;
        tma_unit_cost: number;
      } = this.editedOldGridRow.get(row.data.activity_id) || {
        historical_adjustment_amount: 0,
        tma_amount: 0,
        tma_unit_cost: 0,
      };

      const isUnitChanged = this.rows.unitChanged.has(row.data.activity_id);
      const isTotalChanged = this.rows.totalChanged.has(row.data.activity_id);

      /*
          Each editable column in the vendor estimate section has an associated set.
          Each set contains entries that pair an activity ID with the timestamp of the edit.
          The timestamp allows us to identify the most recently edited column,
          so we can send only the latest change to the backend.
        */
      const allVendorEstimateChanges = [
        ...this.rows.vendorEstimateChanged,
        ...this.rows.vendorEstimatePercentChanged,
        ...this.rows.vendorEstimateAmountLtdChanged,
      ]
        .filter((item) => item.activity_id === row.data.activity_id)
        .sort((a, b) => b.time.diff(a.time));

      const mostRecentVendorEstimateChange = allVendorEstimateChanges[0];

      const isHistoricalAdjustmentChanged = this.rows.historicalAdjustmentChanged.has(
        row.data.activity_id
      );

      const adjustment_type = isUnitChanged
        ? AdjustmentType.ADJUSTMENT_UNIT
        : AdjustmentType.ADJUSTMENT_AMOUNT;

      if (isUnitChanged || isTotalChanged) {
        adjustments[row.data.activity_id] = {
          __typename: 'BudgetExpenseData',
          adjustment_type,
          amount: +row.data.tma_amount,
          id: row.data?.manual_adjustment?.id,
          previous_amount: oldData?.tma_amount,
          unit_cost: oldData?.tma_unit_cost,
          updated_by: this.authQuery?.getValue()?.sub || '',
        };

        adjustmentData.push({
          activity_id: row.data.activity_id,
          adjustment_type,
          amount: +row.data.tma_amount,
          amount_curr: `CURRENCY_${budget_info?.vendor_currency || Currency.USD}`,
          amount_type: `AMOUNT_${row.data.activity_type?.slice(9)}` as AmountType,
          budget_version_id: budget_info?.budget_version_id,
          expense_detail: JSON.stringify({}),
          expense_source: ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT,
          expense_type_id: ExpenseType.EXPENSE_ACCRUAL_OVERRIDE,
          period_end: budget_info?.current_month,
          period_start: budget_info?.current_month,
          source: 'BASE',
        });
      }

      if (isHistoricalAdjustmentChanged) {
        historicalAdjustments[row.data.activity_id] = {
          __typename: 'BudgetExpenseData',
          amount: +row.data.historical_adjustment_amount,
          id: row.data?.historical_adjustment?.id,
          previous_amount: oldData?.historical_adjustment_amount,
          unit_cost: null,
          updated_by: this.authQuery?.getValue()?.sub || '',
        };

        adjustmentData.push({
          activity_id: row.data.activity_id,
          adjustment_type: AdjustmentType.ADJUSTMENT_AMOUNT,
          amount: +row.data.historical_adjustment_amount,
          amount_curr: `CURRENCY_${budget_info?.vendor_currency || Currency.USD}`,
          amount_type: `AMOUNT_${row.data.activity_type?.slice(9)}` as AmountType,
          budget_version_id: budget_info?.budget_version_id,
          expense_detail: JSON.stringify({}),
          expense_source: ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT,
          expense_type_id: ExpenseType.EXPENSE_HISTORICAL_ADJUSTMENT,
          period_end: budget_info?.current_month,
          period_start: budget_info?.current_month,
          source: 'BASE',
        });
      }

      if (mostRecentVendorEstimateChange) {
        vendorEstimateAdjustments[row.data.activity_id] = {
          __typename: 'BudgetExpenseData',
          amount: +row.data.vendor_estimate_amount,
          unit_cost: row.data.direct_cost.unit_cost,
        };

        const { activity_id } = mostRecentVendorEstimateChange;

        let adjustmentEntry;

        if (this.rows.vendorEstimateChanged.has(mostRecentVendorEstimateChange)) {
          adjustmentEntry = {
            activity_id: activity_id,
            adjustment_type: AdjustmentType.ADJUSTMENT_AMOUNT,
            amount: +row.data.vendor_estimate_amount,
            amount_curr: `CURRENCY_${budget_info?.vendor_currency || Currency.USD}`,
            amount_type: `AMOUNT_${row.data.activity_type?.slice(9)}` as AmountType,
            budget_version_id: budget_info?.budget_version_id,
            expense_detail: JSON.stringify({}),
            expense_source: ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE,
            expense_type_id: ExpenseType.EXPENSE_VENDOR_ESTIMATE,
            period_end: budget_info?.current_month,
            period_start: budget_info?.current_month,
            source: 'BASE',
          };
        } else if (this.rows.vendorEstimateAmountLtdChanged.has(mostRecentVendorEstimateChange)) {
          adjustmentEntry = {
            activity_id: activity_id,
            adjustment_type: AdjustmentType.ADJUSTMENT_AMOUNT_LTD,
            amount: +row.data.vendor_estimate_amount_ltd,
            amount_curr: `CURRENCY_${budget_info?.vendor_currency || Currency.USD}`,
            amount_type: `AMOUNT_${row.data.activity_type?.slice(9)}` as AmountType,
            budget_version_id: budget_info?.budget_version_id,
            expense_detail: JSON.stringify({}),
            expense_source: ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE,
            expense_type_id: ExpenseType.EXPENSE_VENDOR_ESTIMATE,
            period_end: budget_info?.current_month,
            period_start: budget_info?.current_month,
            source: 'BASE',
          };
        } else if (this.rows.vendorEstimatePercentChanged.has(mostRecentVendorEstimateChange)) {
          adjustmentEntry = {
            activity_id: activity_id,
            adjustment_type: AdjustmentType.ADJUSTMENT_PERCENTAGE_LTD,
            amount: 0,
            amount_curr: `CURRENCY_${budget_info?.vendor_currency || Currency.USD}`,
            amount_percentage: +row.data.vendor_estimate_percent_ltd,
            amount_type: `AMOUNT_${row.data.activity_type?.slice(9)}` as AmountType,
            budget_version_id: budget_info?.budget_version_id,
            expense_detail: JSON.stringify({}),
            expense_source: ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE,
            expense_type_id: ExpenseType.EXPENSE_VENDOR_ESTIMATE,
            period_end: budget_info?.current_month,
            period_start: budget_info?.current_month,
            source: 'BASE',
          };
        }

        if (adjustmentEntry) {
          adjustmentData.push(adjustmentEntry);
        }
      }
    });

    this.gridDataLoading.set(true);

    const maxBatchSize = 50;
    let { data, errors, success }: GraphqlResponse<batchCreateBudgetExpensesMutation[]> = {
      data: [],
      errors: [],
      success: true,
    };
    let i = 0;

    while (i < adjustmentData.length) {
      const result = await firstValueFrom(
        this.gqlService.batchCreateBudgetExpenses$(adjustmentData.slice(i, i + maxBatchSize))
      );

      success ||= result.success;

      if (result.success && result.data) {
        data = data.concat(result.data);
      } else {
        errors = errors.concat(result.errors);
      }

      i += maxBatchSize;
    }

    if (success && data) {
      this.expenseSourceService.batchOverrideExpenseSources(
        this.selectedVendors,
        this.getFormattedSelectedPeriod(),
        overriddenSourceRows
      );

      await this.expenseSourceService.batchRemoveBudgetExpenses(deletedManuals);

      const activityIds: string[] = [];

      for (const a of adjustmentData) {
        if (a.activity_id) {
          activityIds.push(a.activity_id);
        }
      }

      const vendorEstimateChangesRows = new Set(
        [
          ...this.rows.vendorEstimateChanged,
          ...this.rows.vendorEstimatePercentChanged,
          ...this.rows.vendorEstimateAmountLtdChanged,
        ]
          .reduce((map, item) => map.set(item.activity_id, item), new Map())
          .values()
      );

      // I don't want to update accruals for activities in vendorEstimateChangesRows since they will be updated later
      const filteredActivityIds = activityIds.filter((id) => !vendorEstimateChangesRows.has(id));

      const ids = [...this.historicalAdjustmentToBeDeleted, ...filteredActivityIds];

      const uniqueIdsSet = new Set(ids);

      if (ids.length) {
        // Update accruals for activities that are not in vendorEstimateChangesRows
        // Should send activityIds and vendorId and period only. activity_types should be empty due to conflict with updateAccruals
        await firstValueFrom(
          this.accrualsService.batchUpdateAccruals$(
            this.groupedActivitiesByActivityTypeAndVendor,
            uniqueIdsSet,
            this.getFormattedSelectedPeriod(),
            []
          )
        );
      }

      await firstValueFrom(this.gqlService.invalidateBudgetCache$());
      const vendorEstimateChangedActivityIds = Array.from(vendorEstimateChangesRows).map(
        (item) => item.activity_id
      );

      if (vendorEstimateChangesRows.size > 0) {
        await this.updateVendorEstimateAccruals(new Set(vendorEstimateChangedActivityIds));
      }

      const updatedActivityIds = Array.from(
        new Set([
          ...Array.from(vendorEstimateChangedActivityIds),
          ...this.rows.historicalAdjustmentChanged,
          ...this.rows.sourceChanged,
          ...this.rows.totalChanged,
          ...this.rows.unitChanged,
        ])
      );

      const noteMaxBatchSize = 100;
      const createNoteInputs: CreateNoteInput[] = updatedActivityIds.map((entity_id) => {
        const expense_note_types: ExpenseNoteType[] = [];

        if (this.rows.sourceChanged.has(entity_id)) {
          expense_note_types.push(ExpenseNoteType.EXPENSE_NOTE_EXPENSE_SOURCE_OVERRIDE);
        }

        if (this.rows.historicalAdjustmentChanged.has(entity_id)) {
          expense_note_types.push(ExpenseNoteType.EXPENSE_NOTE_HISTORICAL_ADJUSTMENT);
        }

        if (this.rows.totalChanged.has(entity_id) || this.rows.unitChanged.has(entity_id)) {
          expense_note_types.push(ExpenseNoteType.EXPENSE_NOTE_MANUAL_ADJUSTMENT);
        }

        if (
          Array.from(this.rows.vendorEstimateChanged).some(
            (item) => item.activity_id === entity_id
          ) ||
          Array.from(this.rows.vendorEstimatePercentChanged).some(
            (item) => item.activity_id === entity_id
          ) ||
          Array.from(this.rows.vendorEstimateAmountLtdChanged).some(
            (item) => item.activity_id === entity_id
          )
        ) {
          expense_note_types.push(ExpenseNoteType.EXPENSE_NOTE_VENDOR_ESTIMATE);
        }

        return {
          entity_id,
          entity_type: EntityType.ACTIVITY,
          expense_note_types,
          message: Utils.scrubUserInput(resp.data?.note || ''),
          metadata: JSON.stringify({
            month: dayjs(this.selected_month.value).format('MMM-YYYY').toUpperCase(),
          }),
          note_type: NoteType.NOTE_TYPE_EXPENSE,
        };
      });

      let {
        cData,
        cErrors,
        cSuccess,
      }: {
        cData: batchCreateNotesMutation[] | null;
        cErrors: string[];
        cSuccess: boolean;
      } = {
        cData: [],
        cErrors: [],
        cSuccess: true,
      };
      let j = 0;

      do {
        const result = await firstValueFrom(
          this.gqlService.batchCreateNotes$(createNoteInputs.slice(j, j + noteMaxBatchSize))
        );

        cSuccess ||= result.success;

        if (result.success && result.data) {
          cData = cData.concat(result.data);
        } else {
          cErrors = cErrors.concat(result.errors);
        }

        j += noteMaxBatchSize;
      } while (j < createNoteInputs.length);

      if (cSuccess && cData) {
        this.overlayService.success();
        this.saveCheck$.next(false);
        this.periodCloseComponent.refresh$.next(null);
        this.onCancel();
      } else {
        this.overlayService.error(cErrors);
      }
    } else {
      this.overlayService.error(errors);
    }

    this.gridDataLoading.set(false);
  }

  onSticked(height: number) {
    this.tableHeight.set(height);
  }

  onTrackerIdChanged(trackerId: string) {
    this.budgetTemplateUploadProgressId.set(trackerId);

    if (!trackerId) {
      this.periodCloseComponent.refresh$.next(true);
    }
  }

  onUploadVendorEstimate = async () => {
    const ref = this.overlayService.openPopup<
      undefined,
      { goToEditModeWithCurrentFilters: boolean; month?: string; vendorId?: string },
      AddVendorEstimateUploadComponent
    >({
      content: AddVendorEstimateUploadComponent,
      settings: {
        header: 'Send Vendor Estimate Support to Auxilius',
        primaryButton: {
          action: (instance) => instance?.onUpload(),
          label: 'Send Vendor Estimate Support',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    const isWorkflowLocked = await firstValueFrom(this.isWorkflowLocked$);

    const { goToEditModeWithCurrentFilters, month, vendorId } = resp.data || {};

    if (
      goToEditModeWithCurrentFilters &&
      this.isSelectedMonthOpen$.getValue() &&
      !isWorkflowLocked &&
      !this.isVendorEstimatesLocked$.value
    ) {
      this.onEditMode();
    }

    if (vendorId && month) {
      const queryParamsWithVendorId = {
        currentOpenMonth: dayjs(month).add(2, 'day').format('YYYY-MM-DD'),
        editMode: true,
        vendorId,
      };

      this.router
        .navigate([`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`], {
          queryParams: queryParamsWithVendorId,
        })
        .catch(console.error);
    }

    /*
      give it three seconds to upload before checking for supporting docs.
      If the NEW_TASK appsync notification doesn't reach the FE, this will ensure that we check for supporting docs
    */
    setTimeout(() => {
      this.vendorEstimateSupportingDocUploaded$.next();
    }, 3000);
  };

  onVendorEstimateUploadClick() {
    this.overlayService.openPopup({
      content: VendorEstimateUploadComponent,
      data: {
        isSelectedMonthOpen: this.isSelectedMonthOpen$.getValue(),
        onSuccess: (processEventId: string) => {
          this.budgetTemplateUploadProgressId.set(processEventId);
        },
        selectedMonth: this.selected_month.value,
        selectedVendorIDs: this.selectedVendors,
      },
      settings: {
        header: 'Upload Vendor Estimate',
        primaryButton: {
          action: async (instance: null | VendorEstimateUploadComponent) => {
            await instance?.onUpload();
          },
          label: 'Upload',
        },
      },
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.setTableHeight();
  }

  openNoteDialog = async (event: CellClickedEvent) => {
    const { activity_id, notes } = event.data;

    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        { notes: Note[]; users: Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>> },
        NoteModalResponseType,
        NoteModalComponent
      >({
        content: NoteModalComponent,
        data: {
          notes,
          users: this.users,
        },
        settings: {
          header: 'Add Note',
          primaryButton: {
            action: (instance) => {
              instance?.close(true);
            },
            pendoTag: 'add-note-expense-detail',
          },
        },
      }).afterClosed$
    );

    if (!resp.data?.note) {
      return;
    }

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createNote$({
        entity_id: activity_id,
        entity_type: EntityType.ACTIVITY,
        message: Utils.scrubUserInput(resp.data.note),
        metadata: JSON.stringify({
          month: dayjs(this.selected_month.value).format('MMM-YYYY').toUpperCase(),
        }),
        note_type: NoteType.NOTE_TYPE_GENERAL,
      })
    );

    if (success && data) {
      let rowToUpdate: null | QuarterCloseAdjustmentGridData = null;

      this.overlayService.success();
      this.gridData$.next(
        this.gridData$.getValue().map((row) => {
          if (row.activity_id === activity_id) {
            rowToUpdate = {
              ...row,
              notes: [
                {
                  __typename: 'Note',
                  create_date: dayjs().toISOString(),
                  created_by: this.authQuery.getValue().sub,
                  entity_id: activity_id,
                  entity_type: EntityType.ACTIVITY,
                  id: data.id,
                  message: resp.data?.note,
                  note_type: NoteType.NOTE_TYPE_GENERAL,
                } as Note,
                ...row.notes,
              ],
            };

            return rowToUpdate;
          }

          return row;
        })
      );

      if (rowToUpdate) {
        this.gridApi$.value?.applyTransaction({
          update: [rowToUpdate],
        });
      }

      this.gridApi()!.onFilterChanged();
    } else {
      this.overlayService.error(errors);
    }
  };

  openSupportDocumentsDialog = async (event: CellClickedEvent) => {
    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        { current_month: string; entity_id: string; vendor_id: null | string },
        {
          total_documents?: number;
        },
        SupportModalComponent
      >({
        content: SupportModalComponent,
        data: {
          current_month: this.getFormattedSelectedPeriod(),
          entity_id: event.data.activity_id,
          vendor_id: event.data.organization_id,
        },
        settings: {
          header: 'Upload Support',
          primaryButton: {
            action: (instance) => {
              instance?.onSaveDocuments().catch(console.error);
            },
            pendoTag: 'upload-support-expense-detail',
          },
        },
      }).afterClosed$
    );

    if (isUndefined(resp.data?.total_documents)) {
      return;
    }

    let rowToUpdate: null | QuarterCloseAdjustmentGridData = null;

    this.gridData$.next(
      this.gridData$.getValue().map((row) => {
        if (row.activity_id === event.data.activity_id) {
          rowToUpdate = {
            ...row,
            total_documents: resp.data?.total_documents || 0,
          };
        }

        return row.activity_id === event.data.activity_id
          ? {
              ...row,
              total_documents: resp.data?.total_documents || 0,
            }
          : row;
      })
    );

    if (rowToUpdate) {
      this.gridApi$.value?.applyTransactionAsync({
        update: [rowToUpdate],
      });
    }
  };

  parseExpenses() {
    this.invoiceExpenseSourceFilter.set({});
    this.gridDataProcessing$.next(true);

    const rows = this.inMonthExpenses$.getValue() || [];

    const hasVendorEstimateByVendor = new Set<string>();
    const evidenceBasedExists = new Map<string, boolean>([]);
    const invoicesAmountExists = new Map<string, boolean>([]);

    rows.forEach((row) => {
      if (!hasVendorEstimateByVendor.has(row.organization_id) && row.vendor_estimate !== null) {
        hasVendorEstimateByVendor.add(row.organization_id);
      }

      if (row.evidence_based_to_date?.amount && !evidenceBasedExists.get(row.organization_id)) {
        evidenceBasedExists.set(row.organization_id, true);
      }

      if (row.invoice?.amount && !invoicesAmountExists.get(row.organization_id)) {
        invoicesAmountExists.set(row.organization_id, true);
      }
    });

    const gridData = rows.map((row) => {
      const vendor = this.organizationQuery.getEntity(row.organization_id);
      const doesVendorHasVendorEstimateInSelectedPeriod = hasVendorEstimateByVendor.has(
        row.organization_id
      );

      const total_amount = row.direct_cost?.amount || 0;
      const uom = row.direct_cost?.uom || '';
      const actuals_to_date = row.work_performed?.amount || 0;
      const total_remaining = total_amount - actuals_to_date;
      const isDiscountRow = row.activity_type === ActivityType.ACTIVITY_DISCOUNT;

      const isAutoHistoricalAdjustmentExist = this.isAutoGeneratedHistoricalAdjustment(
        row.historical_adjustment?.adjustment_type
      );

      const getColumnValuesForExpense = (
        exp?: null | Pick<
          BudgetExpenseData,
          | 'amount'
          | 'amount_ltd'
          | 'amount_percentage_ltd'
          | 'expense_source'
          | 'unit_cost'
          | 'unit_num'
        >
      ) => {
        const amount = exp?.amount || 0;
        const unit_cost = exp?.unit_cost || 0;
        const perc = total_amount ? (amount / total_amount) * 100 : 0;
        const unit = exp?.unit_num || (amount && unit_cost ? amount / unit_cost : 0);

        return {
          amount,
          amount_ltd: exp?.amount_ltd,
          amount_percentage_ltd: exp?.amount_percentage_ltd,
          expense_source: exp?.expense_source,
          perc,
          unit: isDiscountRow ? 0 : unit,
          unit_cost: isDiscountRow ? 0 : unit_cost,
        };
      };

      const current_forecast = getColumnValuesForExpense(row.forecast);

      const vendor_estimate = getColumnValuesForExpense(row.vendor_estimate);

      if (row.vendor_estimate) {
        if (row.activity_type === ActivityType.ACTIVITY_SERVICE) {
          this.vendorEstimateSelectableCategories.add(
            `${ActivityType.ACTIVITY_DISCOUNT}_${row.organization_id}`
          );
        }

        if (row.activity_type === ActivityType.ACTIVITY_DISCOUNT) {
          this.vendorEstimateSelectableCategories.add(
            `${ActivityType.ACTIVITY_SERVICE}_${row.organization_id}`
          );
        }

        this.vendorEstimateSelectableCategories.add(`${row.activity_type}_${row.organization_id}`);
      }

      const direct_cost = getColumnValuesForExpense(row.direct_cost);

      let tma_source = doesVendorHasVendorEstimateInSelectedPeriod
        ? ExpenseSource['Vendor Estimate']
        : ExpenseSource.Forecast;

      const prev_month = getColumnValuesForExpense(row.prev_month_work_performed);

      const eba = getColumnValuesForExpense({
        amount: row?.evidence_based?.amount,
        unit_cost: row?.evidence_based?.unit_cost,
      });

      const total_monthly_expense = getColumnValuesForExpense(row.total_monthly_expense);

      /*
        row.manual_adjustment?.amount is just the manual adjustment and does not include historical adjustment,
        so if the monthly expense amount was originally $1000 and the user adjusted it to be $1500,
        manual_adjustment_amount would be $1500. If there was no manual adjustment made, this number will be 0.
      */
      const manual_adjustment_amount = row.manual_adjustment?.amount || 0;

      if (row.manual_adjustment) {
        tma_source = ExpenseSource.Manual;
      }

      if (isDiscountRow) {
        current_forecast.perc = 0;
        vendor_estimate.perc = 0;
        const exp = this.discountExpenses$.getValue();
        const { discount_type } = JSON.parse(
          exp[0]?.manual_adjustment?.expense_detail || '{}'
        ) as DiscountExpenseDetail;

        switch (discount_type) {
          case 'contracted_calculated_discount':
            tma_source = ExpenseSource.Forecast;
            break;
          case 'custom_calculated_discount':
          case 'custom_discount':
          case 'none':
            tma_source = ExpenseSource.Manual;
            break;
          case 'vendor_estimate_discount':
            tma_source = ExpenseSource['Vendor Estimate'];
            break;
        }
      }

      const historical_adjustment_amount = row.historical_adjustment?.amount || 0;

      const accrual = getColumnValuesForExpense(row.accrual);

      // subtracting old amount only if there was a manual adjustment because otherwise total adjustment would just be the historical adjustment
      const total_adjustment = total_monthly_expense.amount - accrual.amount;

      const monthly_expense = row.monthly_expense?.amount || 0;

      const expense_to_date = row.work_performed_to_date?.amount || 0;

      const prev_months_accruals = row.prev_months_accruals?.amount || 0;
      // Backend adds total monthly expense to the expense ltd,
      // so don't we need to add total_monthly_expense when displaying expense ltd for past months
      const expense_ltd_amount =
        expense_to_date +
        (this.isSelectedMonthFuture ? prev_months_accruals : 0) +
        total_monthly_expense.amount;

      const variance_to_forecast = decimalDifference(monthly_expense, current_forecast.amount);

      const extraAttributes =
        row.attributes?.reduce(
          (acc, a) => {
            if (a.attribute_name && a.attribute_value) {
              acc[getEncodedAttributeName(a.attribute_name)] = a.attribute_value;
            }

            return acc;
          },
          {} as Record<string, string>
        ) || {};

      const shouldCalculateVendorEstimatePercentLtdManually =
        !row.vendor_estimate?.amount_percentage_ltd &&
        row.vendor_estimate?.amount_percentage_ltd !== 0;

      const shouldCalculateVendorEstimateAmountLtdManually =
        !row.vendor_estimate?.amount_ltd && row.vendor_estimate?.amount_ltd !== 0;

      if (row.invoice?.amount && row.invoice.amount !== 0) {
        this.invoiceExpenseSourceFilter.update((value) => ({
          ...value,
          [`${row.activity_type}_${row.organization_id}`]: true,
        }));
      }

      const historicalAdjustmentUpdatedBy =
        row.historical_adjustment?.updated_by ||
        (this.authQuery.isAuxAdmin() ? EMPTY_UUID : this.authQuery.getValue()?.sub);

      const data = {
        ...extraAttributes,
        ...row,
        actuals_performed_to_date: row.work_performed_to_date?.amount,
        actuals_to_date,
        canEditVendorEstimate:
          vendor?.receives_vendor_estimate || doesVendorHasVendorEstimateInSelectedPeriod,
        cost_category_name:
          this.defaultCategories.find(({ value }) => value === row.activity_type)?.label || '',
        current_forecast_amount: current_forecast.amount,
        current_forecast_percentage: current_forecast.perc,
        current_forecast_unit: current_forecast.unit,
        evidence_based_amount: eba.amount,
        evidence_based_exist: !!evidenceBasedExists.get(row.organization_id),
        evidence_based_percentage: eba.perc,
        evidence_based_unit: eba.unit,
        expense_ltd: expense_ltd_amount,
        historical_adjustment: isAutoHistoricalAdjustmentExist
          ? {
              adjustment_type: AdjustmentType.ADJUSTMENT_AMOUNT_SYSTEM_GENERATED,
              amount: this.getAutoHistoricalAdjustmentAmount(
                row.evidence_based_to_date?.amount || 0,
                row.work_performed_to_date?.amount || 0,
                row.evidence_based?.amount || 0
              ),
              previous_amount: 0,
              update_date: row.historical_adjustment?.update_date || dayjs().toISOString(),
              updated_by: historicalAdjustmentUpdatedBy,
            }
          : row.historical_adjustment,

        historical_adjustment_amount,
        invoices_amount: row.invoice?.amount || 0,
        invoices_amount_exist: !!invoicesAmountExists.get(row.organization_id),

        invoices_available: this.getInvoicesAvailableNumber(row.organization_id, row.activity_type), // needs for be aggregated rows
        invoices_percentage: 0,
        notes: row.notes,

        prev_month_amount: prev_month.amount,
        prev_month_unit: prev_month.unit,

        previous_months_accruals: prev_months_accruals,
        receives_vendor_estimate: !!vendor?.receives_vendor_estimate,

        tma_amount: monthly_expense,
        tma_percentage:
          row.manual_adjustment || row.historical_adjustment
            ? total_amount
              ? ((total_monthly_expense.amount || manual_adjustment_amount) / total_amount) * 100
              : 0
            : total_monthly_expense.perc,
        tma_source: accrual.expense_source
          ? expenseSourceMapping[accrual.expense_source]
          : tma_source,
        tma_unit:
          row.manual_adjustment || row.historical_adjustment
            ? (manual_adjustment_amount || monthly_expense) /
              (total_monthly_expense.unit_cost || direct_cost?.unit_cost)
            : total_monthly_expense.unit,
        tma_unit_cost:
          row.forecast || row.vendor_estimate
            ? total_monthly_expense.unit_cost || direct_cost?.unit_cost || 0
            : direct_cost?.unit_cost || 0,
        total_adjustment,
        total_amount,
        total_documents: row.documents.length,
        total_monthly_expense: total_monthly_expense.amount,
        total_remaining,

        units: direct_cost.unit,
        uom,
        variance_to_forecast,
        vendor_estimate_amount: vendor_estimate.amount,

        vendor_estimate_amount_ltd: shouldCalculateVendorEstimateAmountLtdManually
          ? this.getVendorEstimateAmountCompleteLTD(
              vendor_estimate.amount,
              row.work_performed_to_date?.amount || 0,
              prev_months_accruals
            )
          : row.vendor_estimate?.amount_ltd,
        vendor_estimate_percent_ltd: shouldCalculateVendorEstimatePercentLtdManually
          ? this.getVendorEstimatePercentCompleteLTD(
              vendor_estimate.amount,
              total_amount,
              row.work_performed_to_date?.amount || 0,
              prev_months_accruals
            )
          : row.vendor_estimate?.amount_percentage_ltd,
        vendor_estimate_percentage: vendor_estimate.perc,
        vendor_estimate_unit: vendor_estimate.unit,

        vendorCurrency: vendor?.currency || Currency.USD,
        vendorName: vendor?.name || '',
      } as QuarterCloseAdjustmentGridData;

      if (isDiscountRow) {
        data.tma_unit = 0;
      }

      return data;
    });

    this.gridData$.next(gridData);
    this.gridApi$.value?.setGridOption('rowData', gridData);

    this.groupedActivitiesByActivityTypeAndVendor = new Map(
      Object.entries(groupBy(gridData, (item) => `${item.activity_type}_${item.organization_id}`))
    );

    this.gridOptions$.next(this.getGridOptions());

    this.gridDataProcessing$.next(false);

    setTimeout(() => {
      this.updateCategoryFiltering();
    }, 0);
  }

  redirectFromEvidenceBasedColumn = () => {
    const month = this.selected_month.value || '';
    const vendors = this.selectedVendors;
    const monthDate = dayjs(month);
    const monthStart = monthDate.startOf('month').format('YYYY-MM-DD');
    const monthEnd = monthDate.endOf('month').format('YYYY-MM-DD');

    this.investigatorTransactionsService.siteMap = (this.sitesQuery.getAll() || []) as Site[];

    this.investigatorTransactionsService.addInMonthAdjustmentsFilters(
      monthStart,
      monthEnd,
      vendors
    );

    this.router.navigate(['/investigator/investigator-transactions']).catch(console.error);
  };

  redirectFromInvoiceColumn = () => {
    const selectedMonth = this.selected_month.value || '';

    if (selectedMonth && this.selectedVendors.length) {
      const servicePeriodFilter = [`${dayjs(selectedMonth).format('YYYY-MM')}-01`];

      this.invoiceService.setAccrualPeriodsAndVendorFilter([], this.selectedVendors);
      this.invoiceService.setSelectedServicePeriodOptions(servicePeriodFilter);
      this.router
        .navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`
        )
        .catch(console.error);
    }
  };

  /**
   * Removes the vendor estimate.
   *
   * This function supports only a single vendor selection. If multiple vendors are selected,
   * only the first vendor's estimate will be removed.
   */
  removeBudgetVendorEstimate = async () => {
    const vendors = this.budgetQuery.getValue().budget_info.map((info) => {
      return {
        label: info.name || '',
        value: info.vendor_id || '',
      };
    });

    if (!this.selectedVendors.length) {
      this.overlayService.error('No Vendor Contract found');

      return;
    }

    const selected_month = dayjs(this.selected_month.value).date(1).format('YYYY-MM-DD');

    const vendor = vendors.find((x) => x.value === this.selectedVendors[0]);

    const formatted_selected_month = formatDate(selected_month, 'MMMM-y', 'en-US');

    const ref = this.overlayService.openPopup({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure you want to remove the ${formatted_selected_month} vendor estimate for ${vendor?.label || Utils.zeroHyphen}?`,
      },
      settings: {
        header: 'Remove Vendor Estimate',
        primaryButton: {
          label: 'Remove',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    if (!this.removeVendorEstimateLoading() && resp.data) {
      this.removeVendorEstimateLoading.set(true);
      const vendorEstimate = this.vendorEstimates$
        .getValue()
        .find((estimate) => estimate.organization_id === vendor?.value);

      if (!vendorEstimate) {
        this.removeVendorEstimateLoading.set(false);
        this.overlayService.error('No Vendor Contract forecast found');

        return;
      }

      this.removeVendorEstimateLoading.set(true);

      const success = await this.budgetService.removeVendorEstimate({
        target_month: selected_month,
        vendor_id: vendorEstimate.organization_id,
      });

      if (success) {
        this.overlayService.success(`Successfully removed vendor estimate`);
      }

      this.removeVendorEstimateLoading.set(false);
    }
  };

  requiredColumnsSelectionValidation = (columns: ColumnChooserNode[]): string | undefined => {
    const isAnyRequiredColumnSelected = columns.some((column) => {
      if (column.children?.length) {
        const childResult = this.requiredColumnsSelectionValidation(column.children);

        return childResult === undefined;
      }

      return (
        this.possibleEditColumns().columns.includes(
          column.field as keyof QuarterCloseAdjustmentGridData
        ) && column.checked
      );
    });

    return isAnyRequiredColumnSelected
      ? undefined
      : MessagesConstants.NO_EDITABLE_COLUMNS_SELECTED_MESSAGE;
  };

  /**
   * Getter to retrieve the selected vendors.
   *
   * Note: To get the selected vendors, you should use only this getter instead of directly accessing the control.
   * This is to maintain backward compatibility with the old vendor dropdown.
   */
  get selectedVendors(): string[] {
    const controlValue = this.selected_vendor.value || [];

    if (this.multipleVendorsActive()) {
      return isArray(controlValue) ? controlValue : [controlValue];
    }

    return isArray(controlValue) ? [controlValue[0]] : [controlValue];
  }

  async selectMonth(event: MouseEvent, value: string) {
    await selectWithGuardChangesModalCheck({
      element: this.monthSelect,
      event,
      formControl: this.selected_month,
      hasUnsavedChanges: this.saveCheck$.value,
      overlayService: this.overlayService,
      value,
    });
  }

  private setSelectedVendor(vendorIDs: string[]) {
    this.selected_vendor.setValue(this.multipleVendorsActive() ? vendorIDs : vendorIDs[0]);
    this.selectedVendorIDs.set(vendorIDs);
  }

  private setTableHeight() {
    const table = document.getElementById('ima_table');

    if (table) {
      const MIN_WINDOW_BREAKPOINT = 700;

      const top = table.getBoundingClientRect().top;

      const calculatedTableHeight = window.innerHeight - top;
      const minHeightInPixels = Math.min(window.innerHeight, MIN_WINDOW_BREAKPOINT);
      const tableHeight =
        Math.max(calculatedTableHeight, minHeightInPixels) -
        FOOTER_HEIGHT -
        this.BOTTOM_CONTAINER_PADDING;

      this.tableHeight.set(tableHeight);
    }
  }

  shouldOverrideSourceToVendorEstimate(row: QuarterCloseAdjustmentGridData) {
    const override = this.shouldVendorEstimateOverride({
      activity_id: row.activity_id,
      current_tma_source: row.tma_source,
      isAffected: true,
      row,
    });

    return override && row.tma_source !== ExpenseSource['Vendor Estimate'];
  }

  shouldVendorEstimateOverride({
    activity_id,
    current_tma_source,
    isAffected = false,
    row,
  }: {
    activity_id: string;
    current_tma_source: ExpenseSource;
    isAffected?: boolean;
    row: QuarterCloseAdjustmentGridData;
  }) {
    const setting = this.expenseSettings.find((s) => s.activity_id === activity_id);

    if (!setting) {
      return false;
    }

    const currentSettingIndex = setting.sources.findIndex(
      (x) => x === expenseSourceReverseMapping[current_tma_source]
    );
    const vendorEstimateIndex = setting.sources.findIndex(
      (x) => x === ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE
    );

    const isVendorEstimateHasHigherPriority = vendorEstimateIndex <= currentSettingIndex;

    let shouldUseVendorEstimate = isVendorEstimateHasHigherPriority;

    if (!isVendorEstimateHasHigherPriority && !isAffected) {
      for (const s of setting.sources.slice(0, vendorEstimateIndex).reverse()) {
        switch (s) {
          case ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED:
            shouldUseVendorEstimate = !this.costCategoryHasEvidenceBased(
              row.activity_type,
              this.gridData$.getValue()
            );
            break;
          case ExpenseSourceType.EXPENSE_SOURCE_FORECAST:
            shouldUseVendorEstimate = !this.costCategoryHasForecasts(
              row.activity_type,
              this.gridData$.getValue()
            );
            break;
        }
      }
    }

    if (current_tma_source === ExpenseSource['Vendor Estimate']) {
      return true;
    }

    return current_tma_source === ExpenseSource.Manual ||
      !setting.default ||
      this.rows.sourceChanged.has(activity_id)
      ? false
      : shouldUseVendorEstimate;
  }

  private syncFilterValuesWithGrid(): void {
    const gridApi = this.gridApi$.value;

    if (!gridApi) return;

    const filterModel = gridApi.getFilterModel();

    // Sync expense source filter values
    const tmaSourceFilter = filterModel.tma_source;

    if (tmaSourceFilter && tmaSourceFilter.filterType === 'set' && tmaSourceFilter.values) {
      this.expenseSourceFilterValues.set(new Set(tmaSourceFilter.values));
    } else {
      // If no filter is applied, all expense sources should be checked
      const allExpenseSources = new Set<string>();

      gridApi.forEachNode((node) => {
        if (node.data && node.data.tma_source) {
          allExpenseSources.add(node.data.tma_source);
        }
      });
      this.expenseSourceFilterValues.set(allExpenseSources);
    }

    // Sync notes filter values
    const notesFilter = filterModel.notes;

    if (notesFilter) {
      const notesFilterValues = new Set<string>();

      if (notesFilter.type === 'greaterThan' && notesFilter.filter === 0) {
        // Show only rows with notes
        notesFilterValues.add('with-notes');
      } else if (notesFilter.type === 'equals' && notesFilter.filter === 0) {
        // Show only rows without notes
        notesFilterValues.add('without-notes');
      }

      this.notesFilterValues.set(notesFilterValues);
    } else {
      // No filter applied - show all rows
      this.notesFilterValues.set(new Set(['with-notes', 'without-notes']));
    }
  }

  triggerReapplyTableEditStyles() {
    const gridApi = this.gridApi$.getValue();

    if (!gridApi) {
      return;
    }

    // Refresh header and cells to ensure edit styles are applied
    gridApi.refreshHeader();
    gridApi.refreshCells({
      columns: [
        'tma_unit',
        'tma_amount',
        'tma_source',
        'historical_adjustment_amount',
        'vendor_estimate_percent_ltd',
        'vendor_estimate_amount',
        'vendor_estimate_amount_ltd',
      ],
      force: true,
    });
  }

  updateBottomData() {
    this.gridApi$
      .getValue()
      ?.setGridOption('pinnedBottomRowData', [this.bottomRowData$.getValue()]);
  }

  updateCategoryFiltering() {
    const gridApi = this.gridApi$.getValue();

    if (gridApi) {
      const filterInstance = gridApi.getFilterInstance('activity_type');

      if (filterInstance) {
        filterInstance.setModel(
          this.selected_category.value
            ? {
                values: [this.selected_category.value],
              }
            : null
        );
      }

      gridApi.onFilterChanged();
    }
  }

  // eslint-disable-next-line max-params
  updateDynamicFields = (
    rawNewValue: number,
    activityId: string,
    colDefField: string | undefined,
    pTmaUnitCost: number,
    pHistoricalAdjustmentAmount: number,
    vendor_estimate_amount: number,
    oldValue: number,
    tmaUnit: number,
    tmaAmount: number,
    totalAmount: number,
    tma_source: ExpenseSource,
    rowData: QuarterCloseAdjustmentGridData
  ) => {
    const newList = this.calculateRow(
      rawNewValue,
      activityId,
      colDefField,
      pTmaUnitCost,
      pHistoricalAdjustmentAmount,
      vendor_estimate_amount,
      oldValue,
      tmaUnit,
      tmaAmount,
      totalAmount,
      tma_source,
      rowData
    );

    this.gridApi$.value?.applyTransactionAsync(
      {
        update: newList,
      },
      () => {
        this.saveCheck$.next(true);

        const updatedGridData = this.gridData$.getValue().map((row) => {
          const updatedRow = newList.find((newRow) => newRow.activity_id === row.activity_id);

          return updatedRow ? updatedRow : row;
        });

        this.gridData$.next(updatedGridData);
      }
    );
  };

  updateFiltersOnRouterQueryParams() {
    combineLatest([
      this.periodCloseComponent.loading$,
      this.gridDataLoading$,
      this.gridDataProcessing$,
      this.route.queryParams,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filterRxjs(
          ([periodCloseLoading, loadingPage, gridDataProcessing]) =>
            periodCloseLoading === false && loadingPage === false && gridDataProcessing === false
        )
      )
      .subscribe(([, , , { currentOpenMonth, vendorId }]) => {
        if (vendorId) {
          this.setSelectedVendor([vendorId]);
        }

        if (currentOpenMonth) {
          this.selected_month.setValue(currentOpenMonth);
        }
      });
  }

  updateMonths() {
    if (this.periodCloseComponent.selectedQuarter.value) {
      const months =
        this.periodCloseComponent.quartersObj[this.periodCloseComponent.selectedQuarter.value];

      const currentMonth = dayjs(this.periodCloseComponent.currentMonth);

      this.months = months.map((m: QuarterDate) => {
        const isClosed = m.parsedDate.isBefore(currentMonth);
        const isOpen = m.parsedDate.date(1).isSame(currentMonth);
        const label = `${m.parsedDate.format('MMMM YYYY')}${isClosed ? ' (Closed)' : ''}${
          isOpen ? ' (Open)' : ''
        }`;

        return { ...m, label };
      });

      return true;
    }

    return false;
  }

  /*
    Since vendor estimate uploads are all or nothing, we need to update every activity under the cost category for the ids provided.
    For example, if just one Services activity is updated, this will call updateAccruals for every Services activity
  */
  async updateVendorEstimateAccruals(activityIdsToSendForUpdate: Set<string>) {
    const doesSelectedMonthHasVendorEstimate = this.doesSelectedMonthHasVendorEstimate();

    const ids = new Set<string>();

    this.gridData$.value?.forEach((row) => {
      if (
        activityIdsToSendForUpdate.has(row.activity_id) &&
        row.tma_source === ExpenseSource['Vendor Estimate']
      ) {
        ids.add(row.activity_id);
      }
    });

    const types = this.getActivitiesTypes(activityIdsToSendForUpdate);

    const isThereServiceInTheTypes = !!types.find(
      (t) => t === ActivityType.ACTIVITY_SERVICE || t === ActivityType.ACTIVITY_DISCOUNT
    );

    const isThereAnyOtherTypes = !!types.find(
      (t) => t !== ActivityType.ACTIVITY_SERVICE && t !== ActivityType.ACTIVITY_DISCOUNT
    );

    let { data, errors, success }: GraphqlResponse<updateAccrualsMutation[]> = {
      data: [],
      errors: [],
      success: true,
    };

    let shouldSendARequestWithIds = true;

    if (!doesSelectedMonthHasVendorEstimate && isThereServiceInTheTypes) {
      // Call on very first time if service/discount changed
      const result = await firstValueFrom(
        this.accrualsService.batchUpdateAccrualsByGroupedVendors$(
          this.groupedActivitiesByActivityTypeAndVendor,
          new Set(activityIdsToSendForUpdate),
          this.getFormattedSelectedPeriod(),
          [ActivityType.ACTIVITY_SERVICE, ActivityType.ACTIVITY_DISCOUNT],
          true
        )
      );

      success ||= result.success;

      if (result.success && result.data) {
        data = data.concat(result.data);
      } else {
        errors = errors.concat(result.errors);
      }

      shouldSendARequestWithIds = isThereAnyOtherTypes;
    }

    if (shouldSendARequestWithIds) {
      // Call on every time if service/discount changed
      const result = await firstValueFrom(
        this.accrualsService.batchUpdateAccrualsByGroupedVendors$(
          this.groupedActivitiesByActivityTypeAndVendor,
          activityIdsToSendForUpdate,
          this.getFormattedSelectedPeriod(),
          []
        )
      );

      success ||= result.success;

      if (result.success && result.data) {
        data = data.concat(result.data);
      } else {
        errors = errors.concat(result.errors);
      }
    }

    if (success) {
      return data;
    } else {
      this.overlayService.error(errors);

      return [];
    }
  }

  vendorEstimateColumnChecker = () => {
    if (
      this.organizationQuery
        .allReceivesEstimateVendors()
        .find((vendor) => this.selectedVendors.includes(vendor.id))
    ) {
      return true;
    } else {
      return !!this.vendorEstimates$
        .getValue()
        .some(
          (vendor) =>
            this.selectedVendors.includes(vendor.organization_id) && vendor.vendor_estimate_exists
        );
    }
  };
}
